<script setup>
import { reactive, onMounted, ref, computed, watch, nextTick } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { LOG_ST_EMBARQUE } from '@/constants/LOG_ST_EMBARQUE'
import { useAuthStore } from '@/stores/auth'
import ComexPipelineStatus from '@/components/base/fluxo/ComexPipelineStatus.vue'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import SemAcessoModulo from '@/components/base/util/SemAcessoModulo.vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { fornecedorSearch } from '@/search-definitions/fornecedorSearch'
import { filialSearch } from '@/search-definitions/filialSearch'
import { loteFabricacaoSearch } from '@/search-definitions/loteFabricacaoSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM'
import { useConversaoQuantidade } from '@/composables/useConversaoQuantidade'
import EmbarquesSmartMode from './EmbarquesSmartMode.vue'
import AssistentePreenchimentoModal from './AssistentePreenchimentoModal.vue'
import DocumentoEntradaGrid from '@/components/Compras/documentoEntrada/DocumentoEntradaGrid.vue'
import SolicitacaoAdiantamentoGrid from '@/components/Financeiro/contasPagarReceber/solicitacaoAdiantamento/SolicitacaoAdiantamentoGrid.vue'
import ExcelGrid from '@/components/base/grid/ExcelGrid.vue'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idEmbarque: 0,
      idOrganizacao: null,
      idFilial: null,
      lstTipoOperacaoComex: '',
      numeroProcessoEmbarque: '',
      numeroBooking: '',
      lstTipoDocumentoTransporte: '',
      numeroDocumentoTransporte: '',
      lstModalTransporte: '',
      lstPaisOrigem: '',
      lstPortoOrigem: '',
      lstPaisDestino: '',
      lstPortoDestino: '',
      idParceiroNegocioTransportador: null,
      idParceiroNegocioAgenteCarga: null,
      nomeVeiculoTransporte: '',
      numeroViagem: '',
      lstIncoterm: '',
      lstLocalIncoterm: '',
      dataColetaNoFornecedor: null,
      dataEmissaoDocumentoTransporte: null,
      dataEmbarqueETD: null,
      dataChegadaETA: null,
      dataAtracacao: null,
      dataPresencaCarga: null,
      isCargaVendidaEmTransito: false,
      idLocalEstoqueDestino: null,
      idLocalEstoqueAtracacao: null,
      lstStatusEmbarque: '',
      idPurchaseOrder: null
    })
  },
  contextoPai: {
    type: Object,
    default: () => ({})
  },
  drilldownMode: {
    type: Boolean,
    default: false
  },
  pipelineProps: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'show-notification',
  'drilldown-pop',
  'pipeline-navegar',
  'drilldown-push'
])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const embarquesCota = ref([])
const faturasEmbarque = ref([])
const faturaSelecionada = ref(null)
const isLoadingFaturas = ref(false)
const isLoadingEmbarques = ref(false)
const showModalFatura = ref(false)
const faturaEmEdicao = ref(null)
const fornecedoresPermitidos = ref(null)
const isLoadingFornecedores = ref(false)

const itensEmbarque = ref([])
const isLoadingItens = ref(false)
const produtosCota = ref([])
const totalEmbarcadoPorCotaProduto = ref({})
const totalEmbarcadoPorCotaProdutoBase = ref({})
const itemEmEdicao = ref(null)
const itemParaExcluir = ref(null)
const confirmModal = ref(null)
const showModalSelecaoProduto = ref(false)
const showModalFormItem = ref(false)
const valoresAtributosNcm = ref({})
const atributosNcm = ref([])
const isLoadingAtributos = ref(false)

// Estado para o modal de atributos NCM na aba Pesos dos Produtos
const showModalAtributosNcmPeso = ref(false)
const itemPesoAtributosEmEdicao = ref(null)
const atributosNcmPeso = ref([])
const valoresAtributosNcmPeso = ref({})
const isLoadingAtributosPeso = ref(false)

// Controle de modo visualização nos modais
const isVisualizacaoModalFatura = ref(false)
const isVisualizacaoModalItem = ref(false)

// Packing List state
const packingListItems = ref([])
const isLoadingPackingList = ref(false)
const packingListConfirmDelete = ref(null)
const packingListConfirmModal = ref(null)
const showModalSelecaoItemPacking = ref(false)
const packingItemEmEdicao = ref(null)

// Containers state
const containerItems = ref([])
const isLoadingContainers = ref(false)
const containerConfirmDelete = ref(null)
const containerConfirmModal = ref(null)

// Baseline maps for change detection per row
const packingListBaselineMap = ref({})
const containerBaselineMap = ref({})

// Assistente IA state
const showAssistentePackingModal = ref(false)
const showAssistenteContainerModal = ref(false)
const showAssistenteDistribuicaoModal = ref(false)

const listas = reactive({})
const mostrarAuditoria = ref(false)

// ============================================================
// ESTADO PARA DOCUMENTOS DE ENTRADA (Lançar Despesas)
// ============================================================
const showModalDocumentosGrid = ref(false)
const documentoSelecionado = ref(null)
const documentoEntradaGridRef = ref(null)

// ============================================================
// ESTADO PARA SOLICITAÇÕES DE ADIANTAMENTO
// ============================================================
const showModalSolicitacaoAdiantamento = ref(false)
const solicitacaoAdiantamentoGridRef = ref(null)

const accordionState = reactive({
  dadosBasicos: false,
  roteamento: false,
  agentes: false,
  incoterm: false,
  prazos: false,
  configuracoes: false
})

const abaAtiva = ref('dadosLogisticos')

const {
  item,
  isLoading,
  isEditing,
  hasChanges,
  errors,
  focarPrimeiroErro,
  initializeForm
} = useFormulario(props, emit)

const isEmbarqueSaved = computed(() => !!(item.value.idEmbarque && item.value.idEmbarque > 0))
const isFromQuota = computed(() => !!props.contextoPai?.idPurchaseOrder)

// Indica se o embarque está em status posterior ao "Pré-embarque".
// Quando true, campos de roteamento/logística tornam-se required dinamicamente.
const isPosPreembarque = computed(() => {
  return item.value.lstStatusEmbarque && item.value.lstStatusEmbarque !== 'LOG_ST_EMBARQUE1'
})

const confirmationModalRef = ref(null)
const pendingAction = ref(null)

const onConfirmTransition = (confirmed) => {
  if (confirmed && pendingAction.value) {
    pendingAction.value()
  }
  pendingAction.value = null
}

const labelProcessoInterno = computed(() => {
  return isEditing.value ? 'Nº Processo Interno' : 'Nº Processo Interno (gerado após salvar)'
})

const labelDataEmissaoDocumentoTransporte = computed(() => {
  const tipoDoc = item.value.lstTipoDocumentoTransporte
  if (tipoDoc) {
    const texto = getTextoLista(listas.LstTipoDocumentoTransporte, tipoDoc)
    return `Data Emissão ${texto}`
  }
  return 'Data Emissão Doc. Transporte'
})

const entityInfo = computed(() => ({
  id: item.value.numeroProcessoEmbarque || item.value.idEmbarque,
  name: item.value.numeroProcessoEmbarque || 'Novo Embarque',
  nameLabel: 'Embarque'
}))
const podeEditar = computed(() => {
  // Modo read-only do pipeline (etapa ultrapassada selecionada para visualização):
  // mesmo com permissão COMPLETO, não permite edição nesta etapa.
  if (props.pipelineProps?.readOnly) return false
  if (authStore.isAdminInterno) return true
  if (authStore.pode('EMBARQUES_COMPLETO')) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('EMBARQUES').some(p => p === 'COMPLETO' || p.includes('COMPLETO'))
})

const temModuloEmbarques = computed(() => {
  return authStore.temModulo('EMBARQUES')
})

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: {
    idEmpresa: authStore.empresaAtiva
  }
}))

const configBuscaLocalEstoque = computed(() =>
  criarConfigBuscaLocalEstoque(authStore.organizacaoAtiva, item.value.idFilial, [ESTQ_TIPO_LOCAL_ESTOCAGEM.Transito])
)

const configBuscaLocalEstoqueTerminal = computed(() =>
  criarConfigBuscaLocalEstoque(authStore.organizacaoAtiva, item.value.idFilial, [ESTQ_TIPO_LOCAL_ESTOCAGEM.AlfandegadoOuEntreposto])
)

const configBuscaTransportador = computed(() => {
  const filtros = { isTransportadora: true }
  if (item.value.lstModalTransporte) {
    filtros.lstModalTransporte = item.value.lstModalTransporte
  }
  return {
    ...parceiroSearch,
    titulo: 'Selecionar Transportador',
    filtrosFixos: filtros
  }
})

const configBuscaAgenteCarga = computed(() => ({
  ...parceiroSearch,
  titulo: 'Selecionar Agente de Carga',
  filtrosFixos: {
    isFornecedor: true,
    lstCategoria: 'PCAT5'
  }
}))

// Configuração de busca de parceiro com categoria PCAT2
const configBuscaParceiroPCat2 = computed(() => ({
  ...parceiroSearch,
  titulo: 'Selecionar Parceiro (Produtor/Fabricante)',
  filtrosFixos: {
    lstCategoria: 'PCAT2'
  }
}))

// Configuração de busca de fornecedor para a fatura — respeita regras de permissão
const configBuscaFornecedorFatura = computed(() => {
  const permissoesEmbarques = authStore.getPermsDoModulo('EMBARQUES')
  const isCompleto = authStore.isAdminInterno ||
    authStore.isAdminCliente ||
    permissoesEmbarques.includes('EMBARQUES_COMPLETO') ||
    permissoesEmbarques.includes('COMPLETO')

  if (isCompleto || !fornecedoresPermitidos.value) {
    return {
      ...fornecedorSearch,
      titulo: 'Selecionar Fornecedor (Emissor da Fatura)',
      filtrosFixos: { isFornecedor: true }
    }
  }

  // Modo restrito: filtrar pelos IDs permitidos
  return {
    ...fornecedorSearch,
    titulo: 'Selecionar Fornecedor (Emissor da Fatura)',
    filtrosFixos: {
      isFornecedor: true,
      ids: fornecedoresPermitidos.value.join(',')
    }
  }
})

// Configuração de busca de lote de fabricação — filtra pelo fornecedor selecionado no form
const configBuscaLoteFabricacao = computed(() => {
  const idFornecedor = itemEmEdicao.value?.idParceiroNegocioProdutor
  if (!idFornecedor) {
    return {
      ...loteFabricacaoSearch,
      titulo: 'Selecione um fornecedor primeiro',
      filtrosFixos: { idFornecedor: -1 }
    }
  }
  return {
    ...loteFabricacaoSearch,
    titulo: 'Buscar Lote de Fabricação',
    filtrosFixos: { idFornecedor }
  }
})

const carregarFaturas = async () => {
  if (!item.value.idEmbarque) {
    faturasEmbarque.value = []
    faturaSelecionada.value = null
    itensEmbarque.value = []
    return
  }

  isLoadingFaturas.value = true
  try {
    // O modelo EmbarqueFaturas foi removido. Os itens agora são gerenciados
    // diretamente via EmbarqueItems com idEmbarque.
    const res = await api.get('/EmbarqueItems', {
      params: { idEmbarque: item.value.idEmbarque, pageSize: 999 }
    })
    itensEmbarque.value = res.data || []

    // Atualiza a "fatura" virtual (agregado único por embarque)
    if (itensEmbarque.value.length > 0) {
      const somaFob = itensEmbarque.value.reduce((acc, item) => {
        return acc + (Number(item.valorTotalMercadoriaFob) || 0)
      }, 0)
      faturaSelecionada.value = {
        idEmbarqueFatura: 0,
        idEmbarque: item.value.idEmbarque,
        valorTotalFatura: somaFob
      }
      faturasEmbarque.value = [faturaSelecionada.value]
    } else {
      faturaSelecionada.value = null
      faturasEmbarque.value = []
    }
  } catch (e) {
    console.error('Erro ao carregar itens do embarque:', e)
    faturasEmbarque.value = []
    faturaSelecionada.value = null
    itensEmbarque.value = []
  } finally {
    isLoadingFaturas.value = false
  }
}

const carregarEmbarquesCota = async () => {
  const idCota = props.contextoPai?.idPurchaseOrder
  if (!idCota) return

  isLoadingEmbarques.value = true
  try {
    const res = await api.get('/Embarques', {
      params: { idPurchaseOrder: idCota }
    })
    embarquesCota.value = res.data
  } catch (e) {
    console.error('Erro ao carregar embarques da cota:', e)
  } finally {
    isLoadingEmbarques.value = false
  }
}

const carregarItensEmbarque = async () => {
  if (!item.value.idEmbarque) {
    itensEmbarque.value = []
    return
  }

  isLoadingItens.value = true
  try {
    const res = await api.get('/EmbarqueItems', {
      params: { idEmbarque: item.value.idEmbarque, pageSize: 999 }
    })
    itensEmbarque.value = res.data
    storeAllPesosBaselines()
  } catch (e) {
    console.error('Erro ao carregar itens do embarque:', e)
  } finally {
    isLoadingItens.value = false
  }
}

// Cache de ControlaLote por idProduto para evitar chamadas repetidas à API
const controlaLoteCache = ref({})

const verificarControlaLote = async (idProduto) => {
  if (!idProduto) return false
  if (controlaLoteCache.value[idProduto] !== undefined) return controlaLoteCache.value[idProduto]
  try {
    const resProd = await api.get(`/Produtos/${idProduto}`)
    const idTipoProduto = resProd.data.idTipoProduto
    if (idTipoProduto) {
      const resTipo = await api.get(`/TiposProduto/${idTipoProduto}`)
      const result = !!resTipo.data.controlaLote
      controlaLoteCache.value[idProduto] = result
      return result
    }
  } catch (e) {
    console.error('Erro ao verificar ControlaLote do produto:', e)
  }
  controlaLoteCache.value[idProduto] = false
  return false
}

const atualizarValorTotalFatura = async () => {
  if (!faturaSelecionada.value) return

  const somaFob = itensEmbarque.value.reduce((acc, item) => {
    return acc + (Number(item.valorTotalMercadoriaFob) || 0)
  }, 0)

  // Atualizar localmente o valor na fatura selecionada e no array
  const index = faturasEmbarque.value.findIndex(f => f.idEmbarqueFatura === faturaSelecionada.value.idEmbarqueFatura)
  if (index >= 0) {
    faturasEmbarque.value[index].valorTotalFatura = somaFob
  }
  faturaSelecionada.value.valorTotalFatura = somaFob
}

const carregarProdutosCota = async () => {
  const idCota = props.contextoPai?.idPurchaseOrder
  if (!idCota) return

  try {
    const res = await api.get(`/CotaProdutos/cota/${idCota}`)
    produtosCota.value = res.data

    const novosTotais = {}
    const novosTotaisBase = {}
    for (const p of produtosCota.value) {
      try {
        const resItens = await api.get('/EmbarqueItems', {
          params: { idCotaProduto: p.idCotaProduto, pageSize: 999 }
        })
        const total = resItens.data.reduce((acc, curr) => acc + (Number(curr.quantidadeEmbarcadaUnFornecedor) || 0), 0)
        novosTotais[p.idCotaProduto] = total

        const totalBase = resItens.data.reduce((acc, curr) => acc + (Number(curr.quantidadeEmbarcadaUnBasica) || 0), 0)
        novosTotaisBase[p.idCotaProduto] = totalBase
      } catch (e) {
        console.error(`Erro ao buscar saldo para produto ${p.idCotaProduto}:`, e)
        novosTotais[p.idCotaProduto] = 0
        novosTotaisBase[p.idCotaProduto] = 0
      }
    }
    totalEmbarcadoPorCotaProduto.value = novosTotais
    totalEmbarcadoPorCotaProdutoBase.value = novosTotaisBase
  } catch (e) {
    console.error('Erro ao carregar produtos da cota:', e)
  }
}

const buscarAtributosNcm = async (codigoNcm) => {
  if (!codigoNcm) {
    atributosNcm.value = []
    return
  }

  isLoadingAtributos.value = true
  try {
    const res = await api.get(`/Ncms/por-codigo/${codigoNcm}`)
    // Filtra apenas atributos de escopo OPERACAO, conforme solicitado.
    // Atributos de escopo PRODUTO são preenchidos no cadastro de produtos.
    atributosNcm.value = (res.data.atributos || []).filter(a => a.escopo === 'OPERACAO')

    // Inicializar o framework dos valores NCM apenas se ainda não foi populado
    // (ex: vindo de edição de item existente, onde já foi populado pelo JSON salvo)
    if (Object.keys(valoresAtributosNcm.value).length === 0) {
      const framework = {}
      for (const attr of atributosNcm.value) {
        framework[attr.codigoSiscomex] = ''
      }
      valoresAtributosNcm.value = framework
    }
  } catch (e) {
    console.error('Erro ao buscar atributos do NCM:', e)
    atributosNcm.value = []
  } finally {
    isLoadingAtributos.value = false
  }
}

const produtosComSaldo = computed(() => {
  return produtosCota.value.map(pc => {
    const alocado = totalEmbarcadoPorCotaProduto.value[pc.idCotaProduto] || 0
    const saldoForn = Math.max(0, (Number(pc.quantidadeUnFornecedorNegociada) || 0) - alocado)

    const alocadoBase = totalEmbarcadoPorCotaProdutoBase.value[pc.idCotaProduto] || 0
    const quantidadeUnBasicaNegociada = Number(pc.quantidadeUnBasicaNegociada) || 0
    const saldoBase = Math.max(0, quantidadeUnBasicaNegociada - alocadoBase)

    return {
      ...pc,
      saldoDisponivel: parseFloat(saldoForn.toFixed(4)),
      saldoDisponivelBase: parseFloat(saldoBase.toFixed(4)),
      lstUnidadeMedidaBasica: pc.lstUnidadeMedidaBasica,
      lstUnidadeMedidaFornecedor: pc.lstUnidadeMedidaFornecedor
    }
  })
})

const saldoProdutoSelecionado = computed(() => {
  if (!itemEmEdicao.value?.idCotaProduto) return null
  return produtosComSaldo.value.find(p => p.idCotaProduto === itemEmEdicao.value.idCotaProduto) || null
})

// Computed para calcular saldo disponível real considerando edição atual
const saldoDisponivelReal = computed(() => {
  if (!saldoProdutoSelecionado.value) return { forn: 0, base: 0 }

  const saldoForn = saldoProdutoSelecionado.value.saldoDisponivel
  const saldoBase = saldoProdutoSelecionado.value.saldoDisponivelBase

  // Se estiver editando um item existente, adicionar a quantidade atual do item
  if (itemEmEdicao.value?.idEmbarqueItem) {
    const itemExistente = itensEmbarque.value.find(ie => ie.idEmbarqueItem === itemEmEdicao.value.idEmbarqueItem)
    if (itemExistente) {
      const qtdExistenteForn = Number(itemExistente.quantidadeEmbarcadaUnFornecedor) || 0
      const qtdExistenteBase = Number(itemExistente.quantidadeEmbarcadaUnBasica) || 0
      return {
        forn: saldoForn + qtdExistenteForn,
        base: saldoBase + qtdExistenteBase
      }
    }
  }
  return { forn: saldoForn, base: saldoBase }
})

// === PESOS COLUMNS ===
const pesosColumns = computed(() => [
  { field: 'skuProduto', label: 'SKU', type: 'text', editable: false, width: '80px' },
  { field: 'nomeProduto', label: 'Nome do Produto', type: 'text', editable: false, width: 'min-width: 200px' },
  { field: 'codigoNcm', label: 'NCM', type: 'text', editable: false, width: '100px' },
  { field: 'numeroDocumentoEntrada', label: 'Nº Doc. Entrada', type: 'text', editable: false, width: '120px' },
  { field: 'nomeProdutor', label: 'Produtor', type: 'text', editable: false, width: 'min-width: 160px' },
  { field: 'nomeFornecedor', label: 'Fornecedor', type: 'text', editable: false, width: 'min-width: 160px' },
  {
    field: 'quantidadeEmbarcadaUnFornecedor',
    label: 'Qtd (Forn)',
    type: 'badge',
    editable: false,
    width: '95px',
    align: 'right',
    badgeMap: true
  },
  {
    field: 'quantidadeEmbarcadaUnBasica',
    label: 'Qtd (Base)',
    type: 'badge',
    editable: false,
    width: '95px',
    align: 'right',
    badgeMap: true
  },
  { field: 'pesoLiquidoDocumentalKg', label: 'Peso Líq (kg)', type: 'number', editable: true, width: '95px', align: 'right', decimals: 4 },
  { field: 'pesoBrutoDocumentalKg', label: 'Peso Bruto (kg)', type: 'number', editable: true, width: '95px', align: 'right', decimals: 4 },
  { field: '_atribNcm', label: 'Atrib. NCM', type: 'actions', editable: false, width: '110px', align: 'center' },
  { field: '_acoes', label: 'Ações', type: 'actions', editable: false, width: '100px', align: 'center' }
])

const onPesoCellEdit = ({ row, value, oldValue }) => {
  if (value === oldValue) return
  // Auto-save peso
  if (row.idEmbarqueItem) {
    row._pesoDirty = true
    salvarPesoItem(row)
  }
}

// === PACKING LIST COMPUTED ===
const packingListColumns = computed(() => [
  { field: 'descricao', label: 'Descrição', type: 'text', editable: true, width: 'min-width: 140px' },
  { field: 'quantidadeVolumes', label: 'Qtd Volumes', type: 'number', editable: true, width: '80px', align: 'center', decimals: 0, required: true },
  { field: 'lstTipoVolume', label: 'Tipos Volumes', type: 'select', editable: true, width: '100px', optionsList: 'LstTipoVolume' },
  { field: 'pesoLiquidoTotalKg', label: 'Peso Líq (kg)', type: 'number', editable: true, width: '100px', align: 'right', required: true },
  { field: 'pesoBrutoTotalKg', label: 'Peso Bruto (kg)', type: 'number', editable: true, width: '100px', align: 'right', required: true },
  { field: 'volumeCBMTotal', label: 'CBM', type: 'number', editable: true, width: '60px', align: 'right' },
  {
    field: 'aiGenerated',
    label: 'IA',
    type: 'badge',
    editable: false,
    width: '50px',
    align: 'center',
    badgeMap: {
      true: { text: 'Sim', class: 'badge bg-info bg-opacity-10 text-info-emphasis' },
      false: { text: 'Não', class: 'text-muted small' }
    }
  },
  { field: '_actions', label: 'Ações', type: 'actions', editable: false, width: '120px', align: 'center' }
])

const packingListRowClass = (row) => {
  if (row.flagPreenchimentoConcluido) return 'row-preenchimento-concluido'
  return ''
}

// === CONTAINER COLUMNS ===
const containerColumns = computed(() => [
  { field: 'numeroContainer', label: 'Nº Container', type: 'text', editable: true, width: 'min-width: 140px' },
  { field: 'lstTipoContainer', label: 'Tipo Container', type: 'select', editable: true, width: '130px', optionsList: 'LstTipoContainer' },
  { field: 'numeroLacre', label: 'Nº Lacre', type: 'text', editable: true, width: 'min-width: 110px' },
  { field: 'pesoTaraKg', label: 'Tara (kg)', type: 'number', editable: true, width: '90px', align: 'right', required: true },
  { field: 'vgmKg', label: 'VGM (kg)', type: 'number', editable: true, width: '90px', align: 'right', required: true },
  {
    field: 'aiGenerated',
    label: 'IA',
    type: 'badge',
    editable: false,
    width: '50px',
    align: 'center',
    badgeMap: {
      true: { text: 'Sim', class: 'badge bg-info bg-opacity-10 text-info-emphasis' },
      false: { text: 'Não', class: 'text-muted small' }
    }
  },
  { field: '_actions', label: 'Ações', type: 'actions', editable: false, width: '100px', align: 'center' }
])

const containerRowClass = (container) => {
  if (container.conteudo?.length > 0) return 'row-container-com-conteudo'
  return ''
}

const onContainerCellEdit = async ({ row, value, oldValue }) => {
  if (value === oldValue) return
  try {
    await salvarItemContainer(row)
  } catch {
    // erro silencioso
  }
}

const onContainerDeleteRow = ({ row }) => {
  const idx = containerItems.value.findIndex(c =>
    (row._tempId && c._tempId === row._tempId) ||
    (row.idEmbarqueContainer && c.idEmbarqueContainer === row.idEmbarqueContainer)
  )
  if (idx >= 0) {
    containerItems.value.splice(idx, 1)
  }
}

const onPackingListCellEdit = async ({ row, value, oldValue }) => {
  // Auto-save: salva automaticamente se o valor mudou
  if (value === oldValue) return
  try {
    await salvarItemPackingList(row)
  } catch {
    // Se o save falhar, o valor já foi alterado no row
    // O usuário pode continuar editando
  }
}

const onPackingListDeleteRow = ({ row }) => {
  // Remove a linha do array local (usado para linhas vazias não salvas)
  const idx = packingListItems.value.findIndex(p =>
    (row._tempId && p._tempId === row._tempId) ||
    (row.idEmbarquePackingList && p.idEmbarquePackingList === row.idEmbarquePackingList)
  )
  if (idx >= 0) {
    packingListItems.value.splice(idx, 1)
  }
}
const parseNumber = (val) => Number(String(val).replace(',', '.')) || 0

const formatarQtd = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '-'
  const num = typeof valor === 'string' ? parseNumber(valor) : Number(valor)
  if (isNaN(num)) return valor
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  }).format(num)
}

// === PESOS DOS PRODUTOS (ABA PESOS) ===
const isSavingPesos = ref(false)

const pesoChangesCount = computed(() => {
  return itensEmbarque.value.filter(i => i._pesoDirty).length
})

const salvarPesoItem = async (itemEmb) => {
  if (isSavingPesos.value || !itemEmb.idEmbarqueItem) return

  const payload = {
    idEmbarque: item.value.idEmbarque,
    idDocumentoEntradaItem: itemEmb.idDocumentoEntradaItem,
    idDocumentoSaidaItem: itemEmb.idDocumentoSaidaItem,
    idProduto: itemEmb.idProduto,
    idLoteFabricacao: itemEmb.idLoteFabricacao,
    quantidadeEmbarcadaUnFornecedor: Number(itemEmb.quantidadeEmbarcadaUnFornecedor) || 0,
    lstUnidadeMedidaFornecedor: itemEmb.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: itemEmb.lstUnidadeMedidaBasica || '',
    fatorConversao: Number(itemEmb.fatorConversao) || 0,
    quantidadeEmbarcadaUnBasica: Number(itemEmb.quantidadeEmbarcadaUnBasica) || 0,
    pesoLiquidoDocumentalKg: itemEmb.pesoLiquidoDocumentalKg !== null && itemEmb.pesoLiquidoDocumentalKg !== undefined && itemEmb.pesoLiquidoDocumentalKg !== ''
      ? Number(itemEmb.pesoLiquidoDocumentalKg)
      : null,
    pesoBrutoDocumentalKg: itemEmb.pesoBrutoDocumentalKg !== null && itemEmb.pesoBrutoDocumentalKg !== undefined && itemEmb.pesoBrutoDocumentalKg !== ''
      ? Number(itemEmb.pesoBrutoDocumentalKg)
      : null
  }

  try {
    isSavingPesos.value = true
    await api.put(`/EmbarqueItems/${itemEmb.idEmbarqueItem}`, payload)
    itemEmb._pesoDirty = false
    storePesosBaseline(itemEmb)
    emit('show-notification', { message: 'Pesos do produto salvos com sucesso!', type: 'success' })
  } catch (e) {
    console.error('Erro ao salvar pesos do produto:', e)
    emit('show-notification', { message: 'Erro ao salvar pesos do produto.', type: 'error' })
  } finally {
    isSavingPesos.value = false
  }
}

const pesosBaselineMap = ref({})

const storePesosBaseline = (itemEmb) => {
  if (!itemEmb.idEmbarqueItem) return
  pesosBaselineMap.value[itemEmb.idEmbarqueItem] = {
    pesoLiquidoDocumentalKg: itemEmb.pesoLiquidoDocumentalKg,
    pesoBrutoDocumentalKg: itemEmb.pesoBrutoDocumentalKg
  }
}

const storeAllPesosBaselines = () => {
  pesosBaselineMap.value = {}
  for (const itemEmb of itensEmbarque.value) {
    storePesosBaseline(itemEmb)
  }
}

const cancelarPesoItem = (itemEmb) => {
  if (!itemEmb.idEmbarqueItem) return
  const baseline = pesosBaselineMap.value[itemEmb.idEmbarqueItem]
  if (baseline) {
    itemEmb.pesoLiquidoDocumentalKg = baseline.pesoLiquidoDocumentalKg
    itemEmb.pesoBrutoDocumentalKg = baseline.pesoBrutoDocumentalKg
  }
  itemEmb._pesoDirty = false
}

const salvarTodosPesos = async () => {
  if (isSavingPesos.value) return

  const itensDirty = itensEmbarque.value.filter(i => i._pesoDirty)
  if (itensDirty.length === 0) {
    emit('show-notification', { message: 'Nenhuma alteração pendente para salvar.', type: 'info' })
    return
  }

  let sucessos = 0
  let erros = 0

  isSavingPesos.value = true
  try {
    for (const itemEmb of itensDirty) {
      try {
        const payload = {
          idEmbarque: item.value.idEmbarque,
          idDocumentoEntradaItem: itemEmb.idDocumentoEntradaItem,
          idDocumentoSaidaItem: itemEmb.idDocumentoSaidaItem,
          idProduto: itemEmb.idProduto,
          idLoteFabricacao: itemEmb.idLoteFabricacao,
          quantidadeEmbarcadaUnFornecedor: Number(itemEmb.quantidadeEmbarcadaUnFornecedor) || 0,
          lstUnidadeMedidaFornecedor: itemEmb.lstUnidadeMedidaFornecedor || '',
          lstUnidadeMedidaBasica: itemEmb.lstUnidadeMedidaBasica || '',
          fatorConversao: Number(itemEmb.fatorConversao) || 0,
          quantidadeEmbarcadaUnBasica: Number(itemEmb.quantidadeEmbarcadaUnBasica) || 0,
          pesoLiquidoDocumentalKg: itemEmb.pesoLiquidoDocumentalKg !== null && itemEmb.pesoLiquidoDocumentalKg !== undefined && itemEmb.pesoLiquidoDocumentalKg !== ''
            ? Number(itemEmb.pesoLiquidoDocumentalKg)
            : null,
          pesoBrutoDocumentalKg: itemEmb.pesoBrutoDocumentalKg !== null && itemEmb.pesoBrutoDocumentalKg !== undefined && itemEmb.pesoBrutoDocumentalKg !== ''
            ? Number(itemEmb.pesoBrutoDocumentalKg)
            : null
        }
        await api.put(`/EmbarqueItems/${itemEmb.idEmbarqueItem}`, payload)
        itemEmb._pesoDirty = false
        storePesosBaseline(itemEmb)
        sucessos++
      } catch (e) {
        console.error('Erro ao salvar pesos do item:', e)
        erros++
      }
    }

    if (erros === 0) {
      emit('show-notification', { message: `Pesos salvos com sucesso! (${sucessos} produtos)`, type: 'success' })
    } else {
      emit('show-notification', { message: `Salvamento parcial: ${sucessos} sucessos, ${erros} erros.`, type: 'warning' })
    }
  } finally {
    isSavingPesos.value = false
  }
}

// ============================================================
// ATRIBUTOS NCM NA ABA PESOS DOS PRODUTOS
// ============================================================
const temAtributosNcmPreenchidos = (itemEmb) => {
  if (!itemEmb.atributosDinamicosPortalUnicoJson) return false
  try {
    const parsed = JSON.parse(itemEmb.atributosDinamicosPortalUnicoJson)
    if (!parsed || typeof parsed !== 'object') return false
    // Verifica se existe pelo menos um valor não vazio
    return Object.values(parsed).some(v => v !== null && v !== undefined && String(v).trim() !== '')
  } catch (e) {
    return false
  }
}

const abrirModalAtributosNcmPeso = async (itemEmb) => {
  itemPesoAtributosEmEdicao.value = itemEmb

  // Carregar atributos do NCM do produto
  let codigoNcm = itemEmb.codigoNcm
  if (!codigoNcm) {
    try {
      const resProd = await api.get(`/Produtos/${itemEmb.idProduto}`)
      codigoNcm = resProd.data.codigoNcm
    } catch (e) {
      console.error('Erro ao buscar NCM do produto:', e)
    }
  }

  if (!codigoNcm) {
    emit('show-notification', { message: 'Produto sem NCM definido.', type: 'warning' })
    return
  }

  isLoadingAtributosPeso.value = true
  try {
    const res = await api.get(`/Ncms/por-codigo/${codigoNcm}`)
    // Filtra apenas atributos de escopo OPERACAO
    atributosNcmPeso.value = (res.data.atributos || []).filter(a => a.escopo === 'OPERACAO')

    // Inicializar valores
    if (itemEmb.atributosDinamicosPortalUnicoJson) {
      try {
        valoresAtributosNcmPeso.value = JSON.parse(itemEmb.atributosDinamicosPortalUnicoJson)
      } catch (e) {
        valoresAtributosNcmPeso.value = {}
      }
    } else {
      const framework = {}
      for (const attr of atributosNcmPeso.value) {
        framework[attr.codigoSiscomex] = ''
      }
      valoresAtributosNcmPeso.value = framework
    }
  } catch (e) {
    console.error('Erro ao buscar atributos do NCM:', e)
    atributosNcmPeso.value = []
    emit('show-notification', { message: 'Erro ao carregar atributos do NCM.', type: 'error' })
    return
  } finally {
    isLoadingAtributosPeso.value = false
  }

  showModalAtributosNcmPeso.value = true
}

const salvarAtributosNcmPeso = async () => {
  if (!itemPesoAtributosEmEdicao.value || !itemPesoAtributosEmEdicao.value.idEmbarqueItem) return

  const payload = {
    idEmbarque: item.value.idEmbarque,
    idDocumentoEntradaItem: itemPesoAtributosEmEdicao.value.idDocumentoEntradaItem,
    idDocumentoSaidaItem: itemPesoAtributosEmEdicao.value.idDocumentoSaidaItem,
    idProduto: itemPesoAtributosEmEdicao.value.idProduto,
    idLoteFabricacao: itemPesoAtributosEmEdicao.value.idLoteFabricacao,
    quantidadeEmbarcadaUnFornecedor: Number(itemPesoAtributosEmEdicao.value.quantidadeEmbarcadaUnFornecedor) || 0,
    lstUnidadeMedidaFornecedor: itemPesoAtributosEmEdicao.value.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: itemPesoAtributosEmEdicao.value.lstUnidadeMedidaBasica || '',
    fatorConversao: Number(itemPesoAtributosEmEdicao.value.fatorConversao) || 0,
    quantidadeEmbarcadaUnBasica: Number(itemPesoAtributosEmEdicao.value.quantidadeEmbarcadaUnBasica) || 0,
    pesoLiquidoDocumentalKg: itemPesoAtributosEmEdicao.value.pesoLiquidoDocumentalKg !== null && itemPesoAtributosEmEdicao.value.pesoLiquidoDocumentalKg !== undefined && itemPesoAtributosEmEdicao.value.pesoLiquidoDocumentalKg !== ''
      ? Number(itemPesoAtributosEmEdicao.value.pesoLiquidoDocumentalKg)
      : null,
    pesoBrutoDocumentalKg: itemPesoAtributosEmEdicao.value.pesoBrutoDocumentalKg !== null && itemPesoAtributosEmEdicao.value.pesoBrutoDocumentalKg !== undefined && itemPesoAtributosEmEdicao.value.pesoBrutoDocumentalKg !== ''
      ? Number(itemPesoAtributosEmEdicao.value.pesoBrutoDocumentalKg)
      : null,
    atributosDinamicosPortalUnicoJson: JSON.stringify(valoresAtributosNcmPeso.value)
  }

  try {
    await api.put(`/EmbarqueItems/${itemPesoAtributosEmEdicao.value.idEmbarqueItem}`, payload)
    // Atualizar o item na lista local
    itemPesoAtributosEmEdicao.value.atributosDinamicosPortalUnicoJson = payload.atributosDinamicosPortalUnicoJson
    showModalAtributosNcmPeso.value = false
    emit('show-notification', { message: 'Atributos NCM salvos com sucesso!', type: 'success' })
  } catch (e) {
    console.error('Erro ao salvar atributos NCM:', e)
    emit('show-notification', { message: 'Erro ao salvar atributos NCM.', type: 'error' })
  }
}

// Setters simples (o cálculo é feito pelo backend no lost focus / botão Calcular)
const onQtdFornecedorUpdate = (val) => {
  if (!itemEmEdicao.value) return
  itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor = val
}

const onQtdBasicaUpdate = (val) => {
  if (!itemEmEdicao.value) return
  itemEmEdicao.value.quantidadeEmbarcadaUnBasica = val
}

const { simular } = useConversaoQuantidade()

// Conversão centralizada (backend = fonte da verdade).
// Chamada no lost focus do campo de quantidade ou no botão "Calcular".
const calcularConversao = async (campo) => {
  const item = itemEmEdicao.value
  if (!item || !item.idProduto) return

  try {
    const resultado = await simular({
      idProduto: item.idProduto,
      idFornecedor: item.idFornecedor || null,
      quantidadeFornecedor: parseNumber(item.quantidadeEmbarcadaUnFornecedor) || 0,
      quantidadeBase: parseNumber(item.quantidadeEmbarcadaUnBasica) || 0,
      fatorConversao: parseNumber(item.fatorConversao) || null,
      campoOrigem: campo === 'base' ? 'Base' : 'Fornecedor'
    })
    item.quantidadeEmbarcadaUnFornecedor = resultado.quantidadeFornecedor
    item.quantidadeEmbarcadaUnBasica = resultado.quantidadeBase
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao calcular a conversão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const getTextoUnidadeCompleto = (idLista, id) => {
  const lista = listas[idLista] || []
  const un = lista.find(u => u.idListaClienteItem === id)
  if (!un) return id || '-'
  return `${un.textoLista} - ${un.descricaoItem || un.textoLista}`
}

const _iniciarNovoItem = () => {
  if (!isFromQuota.value || !faturaSelecionada.value) return

  itemEmEdicao.value = {
    idEmbarqueItem: 0,
    idEmbarqueFatura: faturaSelecionada.value.idEmbarqueFatura,
    idCotaProduto: null,
    idProduto: null,
    nomeProduto: '',
    sku: '',
    idParceiroNegocioProdutor: null,
    idLoteFabricacao: null,
    numeroLoteFornecedor: '',
    controlaLote: false,
    atributosDinamicosPortalUnicoJson: null,
    quantidadeEmbarcadaUnBasica: null,
    quantidadeEmbarcadaUnFornecedor: null,
    // Padronizar nomenclatura com backend
    lstUnidadeMedidaBasica: '',
    lstUnidadeMedidaFornecedor: '',
    // Manter campos antigos para compatibilidade (serão mapeados)
    unidadeMedidaBasica: '',
    unidadeMedidaFornecedor: '',
    precoUnitarioMercadoriaFob: 0,
    valorTotalMercadoriaFob: 0,
    // Novo campo
    fatorConversao: null
  }
  valoresAtributosNcm.value = {}
  showModalSelecaoProduto.value = true
}

const selecionarProdutoDaCota = async (prod) => {
  console.log('selecionarProdutoDaCota: Objeto prod completo:', prod) // Log de depuração para o objeto completo
  itemEmEdicao.value.idCotaProduto = prod.idCotaProduto
  console.log('selecionarProdutoDaCota: prod.idProduto ANTES da atribuição:', prod.idProduto) // Log de depuração
  itemEmEdicao.value.idProduto = prod.idProduto
  itemEmEdicao.value.nomeProduto = prod.nomeProduto
  itemEmEdicao.value.sku = prod.sku
  // Padronizar nomenclatura com backend
  itemEmEdicao.value.lstUnidadeMedidaBasica = prod.lstUnidadeMedidaBasica
  itemEmEdicao.value.lstUnidadeMedidaFornecedor = prod.lstUnidadeMedidaFornecedor
  // Manter campos antigos para compatibilidade
  itemEmEdicao.value.unidadeMedidaBasica = prod.lstUnidadeMedidaBasica
  itemEmEdicao.value.unidadeMedidaFornecedor = prod.lstUnidadeMedidaFornecedor
  itemEmEdicao.value.precoUnitarioMercadoriaFob = prod.precoUnitarioUnFornecedorAcordado
  // Carregar fator de conversão do produto da cota
  itemEmEdicao.value.fatorConversao = prod.fatorConversao || null

  // Resetar valores de atributos NCM para um novo item
  valoresAtributosNcm.value = {}

  let codigoNcm = prod.codigoNcm
  if (!codigoNcm) {
    try {
      console.log('selecionarProdutoDaCota: idProduto na chamada da API /Produtos/ (dentro do try):', prod.idProduto) // Log de depuração
      const resProd = await api.get(`/Produtos/${prod.idProduto}`)
      codigoNcm = resProd.data.codigoNcm
    } catch (e) {
      console.error('Erro ao buscar NCM do produto:', e)
    }
  }

  await buscarAtributosNcm(codigoNcm)

  // Verificar ControlaLote do produto e definir visibilidade do campo lote
  itemEmEdicao.value.controlaLote = await verificarControlaLote(prod.idProduto)

  showModalSelecaoProduto.value = false
  showModalFormItem.value = true
  // Chamar conversão inicial se houver quantidade predefinida
  if (itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor) {
    onQtdFornecedorUpdate(itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor)
  }
}

const salvarItem = async () => {
  if (isLoadingItens.value) return

  // Validação manual de campos obrigatórios do item
  if (!itemEmEdicao.value.idProduto) {
    emit('show-notification', { message: 'Erro: ID do Produto não identificado para o item.', type: 'error' })
    return
  }

  if (!itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor || Number(itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor) <= 0) {
    emit('show-notification', { message: 'Por favor, informe uma quantidade válida maior que zero.', type: 'warning' })
    return
  }

  // Validação de saldo disponível (apenas se vem de cota)
  if (itemEmEdicao.value.idCotaProduto && saldoDisponivelReal.value) {
    const qtdForn = Number(itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor)
    const saldoForn = saldoDisponivelReal.value.forn
    if (qtdForn > saldoForn) {
      emit('show-notification', {
        message: `Saldo insuficiente! Quantidade solicitada: ${formatarQtd(qtdForn)} ${getTextoLista(listas.LstUnidadeMedidaFornecedor, itemEmEdicao.value.lstUnidadeMedidaFornecedor)}. Saldo disponível: ${formatarQtd(saldoForn)}.`,
        type: 'error'
      })
      return
    }
  }

  // Validação de atributos NCM obrigatórios
  const atributosObrigatorios = atributosNcm.value.filter(a => a.obrigatorio)
  for (const attr of atributosObrigatorios) {
    const valor = (valoresAtributosNcm.value[attr.codigoSiscomex] || '').toString().trim()
    if (!valor) {
      emit('show-notification', {
        message: `O atributo NCM "${attr.descricao}" (${attr.codigoSiscomex}) é obrigatório.`,
        type: 'warning'
      })
      return
    }
  }

  // Conversão explícita para número para evitar envio de strings
  itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor = Number(itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor)
  itemEmEdicao.value.quantidadeEmbarcadaUnBasica = Number(itemEmEdicao.value.quantidadeEmbarcadaUnBasica) || 0
  itemEmEdicao.value.precoUnitarioMercadoriaFob = Number(itemEmEdicao.value.precoUnitarioMercadoriaFob)
  itemEmEdicao.value.fatorConversao = itemEmEdicao.value.fatorConversao ? Number(itemEmEdicao.value.fatorConversao) : null

  // Garantir que campos padronizados sejam enviados (mapear campos antigos para novos)
  if (!itemEmEdicao.value.lstUnidadeMedidaBasica && itemEmEdicao.value.unidadeMedidaBasica) {
    itemEmEdicao.value.lstUnidadeMedidaBasica = itemEmEdicao.value.unidadeMedidaBasica
  }
  if (!itemEmEdicao.value.lstUnidadeMedidaFornecedor && itemEmEdicao.value.unidadeMedidaFornecedor) {
    itemEmEdicao.value.lstUnidadeMedidaFornecedor = itemEmEdicao.value.unidadeMedidaFornecedor
  }

  itemEmEdicao.value.atributosDinamicosPortalUnicoJson = JSON.stringify(valoresAtributosNcm.value)
  itemEmEdicao.value.valorTotalMercadoriaFob = itemEmEdicao.value.quantidadeEmbarcadaUnFornecedor * itemEmEdicao.value.precoUnitarioMercadoriaFob

  try {
    isLoadingItens.value = true
    if (itemEmEdicao.value.idEmbarqueItem > 0) {
      await api.put(`/EmbarqueItems/${itemEmEdicao.value.idEmbarqueItem}`, itemEmEdicao.value)
    } else {
      await api.post('/EmbarqueItems', itemEmEdicao.value)
    }

    emit('show-notification', { message: 'Item do embarque salvo com sucesso!', type: 'success' })
    showModalFormItem.value = false
    await carregarItensEmbarque()
    await atualizarValorTotalFatura()
    await carregarProdutosCota()
  } catch (e) {
    console.error('Erro ao salvar item:', e)
    emit('show-notification', { message: 'Erro ao salvar item do embarque.', type: 'error' })
  } finally {
    isLoadingItens.value = false
  }
}

const confirmarExclusaoItem = async (confirmado) => {
  if (!confirmado || !itemParaExcluir.value) {
    itemParaExcluir.value = null
    return
  }

  try {
    await api.delete(`/EmbarqueItems/${itemParaExcluir.value}`)
    emit('show-notification', { message: 'Item excluído com sucesso!', type: 'success' })
    await carregarItensEmbarque()
    await atualizarValorTotalFatura()
    await carregarProdutosCota()
  } catch (e) {
    console.error('Erro ao excluir item:', e)
    emit('show-notification', { message: 'Erro ao excluir item.', type: 'error' })
  } finally {
    itemParaExcluir.value = null
  }
}

// === FATURAS METHODS ===
const salvarFaturaModal = async () => {
  if (!faturaEmEdicao.value) return
  emit('show-notification', { message: 'Faturamento descontinuado. Gerencie os itens diretamente.', type: 'info' })
}

// ============================================================
// FORNECEDORES PERMITIDOS
// ============================================================
const carregarFornecedoresPermitidos = async () => {
  const permissoesEmbarques = authStore.getPermsDoModulo('EMBARQUES')
  const isCompleto = authStore.isAdminInterno ||
    authStore.isAdminCliente ||
    permissoesEmbarques.includes('EMBARQUES_COMPLETO') ||
    permissoesEmbarques.includes('COMPLETO')

  if (isCompleto) {
    fornecedoresPermitidos.value = null
    return
  }

  const idCota = item.value.idPurchaseOrder || props.contextoPai?.idPurchaseOrder
  if (!idCota) {
    fornecedoresPermitidos.value = []
    return
  }

  isLoadingFornecedores.value = true
  try {
    const resCota = await api.get(`/PurchaseOrders/${idCota}`)
    const idFornecedor = resCota.data?.idParceiroNegocioFornecedor

    if (!idFornecedor) {
      fornecedoresPermitidos.value = []
      return
    }

    const resFornec = await api.get(`/Parceiros/${idFornecedor}`)
    const fornecedor = resFornec.data
    const idGrupoEmpresa = fornecedor.idGrupoEmpresa

    let idsPermitidos = [idFornecedor]

    if (idGrupoEmpresa) {
      const resGrupo = await api.get('/Parceiros', {
        params: {
          isFornecedor: true,
          idGrupoEmpresa: idGrupoEmpresa,
          pageSize: 999
        }
      })
      const parceirosGrupo = resGrupo.data || []
      const idsGrupo = parceirosGrupo.map(p => p.idParceiro)
      idsPermitidos = [...new Set([...idsPermitidos, ...idsGrupo])]
    }

    fornecedoresPermitidos.value = idsPermitidos
  } catch (e) {
    console.error('Erro ao carregar fornecedores permitidos:', e)
    fornecedoresPermitidos.value = [props.contextoPai?.idParceiroNegocioFornecedor].filter(Boolean)
  } finally {
    isLoadingFornecedores.value = false
  }
}

// === PACKING LIST CHANGE DETECTION ===
const PACKING_LIST_TRACKED_FIELDS = [
  'descricao', 'quantidadeVolumes', 'lstTipoVolume',
  'pesoLiquidoTotalKg', 'pesoBrutoTotalKg', 'volumeCBMTotal'
]

function packingListTrackedFields (item) {
  const obj = {}
  for (const field of PACKING_LIST_TRACKED_FIELDS) {
    obj[field] = item[field] ?? ''
  }
  return JSON.stringify(obj)
}

function buildPackingListKey (item) {
  return item.idEmbarquePackingList || item._tempId || ''
}

function storePackingListBaseline (item) {
  const key = buildPackingListKey(item)
  if (key) {
    packingListBaselineMap.value[key] = packingListTrackedFields(item)
  }
}

function storeAllPackingListBaselines () {
  packingListBaselineMap.value = {}
  for (const item of packingListItems.value) {
    storePackingListBaseline(item)
  }
}

function hasPackingListChanges (item) {
  const key = buildPackingListKey(item)
  const baseline = packingListBaselineMap.value[key]
  // Linha nova nunca salva → sempre mostra ações (salvar/remover)
  if (!baseline && (!item.idEmbarquePackingList || item.idEmbarquePackingList === 0)) {
    return true
  }
  if (baseline) {
    return packingListTrackedFields(item) !== baseline
  }
  return false
}

function hasAnyPackingListChanges () {
  return packingListItems.value.some(item => hasPackingListChanges(item))
}

function cancelPackingListRow (packItem) {
  const key = buildPackingListKey(packItem)
  const baseline = packingListBaselineMap.value[key]
  // Linha nova nunca salva → remover
  if (!baseline && (!packItem.idEmbarquePackingList || packItem.idEmbarquePackingList === 0)) {
    const idx = packingListItems.value.findIndex(p => p._tempId === packItem._tempId)
    if (idx >= 0) packingListItems.value.splice(idx, 1)
    return
  }
  // Linha existente → restaurar baseline
  if (baseline) {
    const original = JSON.parse(baseline)
    Object.assign(packItem, original)
  }
}

function cancelAllPackingList () {
  const toRemove = []
  packingListItems.value.forEach((item, idx) => {
    const key = buildPackingListKey(item)
    const hasBaseline = !!packingListBaselineMap.value[key]
    if (!hasBaseline && (!item.idEmbarquePackingList || item.idEmbarquePackingList === 0)) {
      toRemove.push(idx)
    } else {
      cancelPackingListRow(item)
    }
  })
  // Remove de trás para frente para não quebrar índices
  toRemove.sort((a, b) => b - a).forEach(idx => packingListItems.value.splice(idx, 1))
}

// === PACKING LIST METHODS ===
const carregarPackingList = async () => {
  if (!item.value.idEmbarque) {
    packingListItems.value = []
    storeAllPackingListBaselines()
    return
  }
  isLoadingPackingList.value = true
  try {
    console.log('Carregando packing list para embarque:', item.value.idEmbarque)
    const res = await api.get(`/EmbarquePackingLists/embarque/${item.value.idEmbarque}`)
    console.log('Resposta packing list:', res.data)
    packingListItems.value = res.data || []
    storeAllPackingListBaselines()
  } catch (e) {
    console.error('Erro ao carregar packing list:', e)
    packingListItems.value = []
    storeAllPackingListBaselines()
  } finally {
    isLoadingPackingList.value = false
  }
}

const adicionarLinhaPackingList = () => {
  const tempId = Date.now() + Math.random()
  packingListItems.value.push({
    _tempId: tempId,
    idEmbarquePackingList: 0,
    idEmbarque: item.value.idEmbarque,
    idEmbarqueContainer: null,
    quantidadeVolumes: null,
    lstTipoVolume: 'BUNDLES',
    pesoLiquidoTotalKg: null,
    pesoBrutoTotalKg: null,
    volumeCBMTotal: null,
    aiGenerated: false,
    descricao: '',
    flagPreenchimentoConcluido: false
  })
}

const selecionarItemParaPackingList = (itemEmb) => {
  if (packingItemEmEdicao.value) {
    // Alterando item existente do packing list
    const packItem = packingItemEmEdicao.value
    packItem.idEmbarqueItem = itemEmb.idEmbarqueItem
    packItem.idProduto = itemEmb.idProduto
    packItem.descricaoProduto = itemEmb.nomeProduto || `Produto #${itemEmb.idProduto}`
    packItem.sku = itemEmb.sku || ''
    packItem.lstUnidadeMedidaBasica = itemEmb.lstUnidadeMedidaBasica || itemEmb.unidadeMedidaBasica || ''
    packItem.lstUnidadeMedidaFornecedor = itemEmb.lstUnidadeMedidaFornecedor || itemEmb.unidadeMedidaFornecedor || ''
    packItem.quantidadeEmbarcadaUnBasica = Number(itemEmb.quantidadeEmbarcadaUnBasica) || 0
    packItem.quantidadeEmbarcadaUnFornecedor = Number(itemEmb.quantidadeEmbarcadaUnFornecedor) || 0
    packItem.fatorConversao = itemEmb.fatorConversao || null
  } else {
    // Adicionando novo item ao packing list
    const tempId = Date.now() + Math.random()
    packingListItems.value.push({
      _tempId: tempId,
      idEmbarquePackingList: 0,
      idEmbarque: item.value.idEmbarque,
      idEmbarqueContainer: null,
      idEmbarqueItem: itemEmb.idEmbarqueItem,
      idProduto: itemEmb.idProduto,
      quantidadeVolumes: null,
      lstTipoVolume: 'BUNDLES',
      pesoLiquidoTotalKg: null,
      pesoBrutoTotalKg: null,
      volumeCBMTotal: null,
      quantidadeEmbarcadaUnBasica: Number(itemEmb.quantidadeEmbarcadaUnBasica) || 0,
      quantidadeEmbarcadaUnFornecedor: Number(itemEmb.quantidadeEmbarcadaUnFornecedor) || 0,
      lstUnidadeMedidaBasica: itemEmb.lstUnidadeMedidaBasica || itemEmb.unidadeMedidaBasica || '',
      lstUnidadeMedidaFornecedor: itemEmb.lstUnidadeMedidaFornecedor || itemEmb.unidadeMedidaFornecedor || '',
      fatorConversao: itemEmb.fatorConversao || null,
      aiGenerated: false,
      aiDescription: null,
      flagPreenchimentoConcluido: false,
      descricaoProduto: itemEmb.nomeProduto || `Produto #${itemEmb.idProduto}`,
      sku: itemEmb.sku || ''
    })
  }
  showModalSelecaoItemPacking.value = false
  packingItemEmEdicao.value = null
}

const salvarItemPackingList = async (packItem) => {
  // Valida campos numéricos obrigatórios antes de salvar
  const camposObrigatorios = [
    { campo: 'quantidadeVolumes', label: 'Quantidade de Volumes' },
    { campo: 'pesoLiquidoTotalKg', label: 'Peso Líquido (kg)' },
    { campo: 'pesoBrutoTotalKg', label: 'Peso Bruto (kg)' }
  ]
  for (const { campo, label } of camposObrigatorios) {
    const val = packItem[campo]
    if (val === null || val === undefined || val === '' || Number(val) === 0) {
      emit('show-notification', {
        message: `O campo "${label}" é obrigatório. Preencha-o antes de salvar.`,
        type: 'warning'
      })
      return
    }
  }

  try {
    isLoadingPackingList.value = true
    let response

    if (packItem.idEmbarquePackingList > 0) {
      // EDIÇÃO: Usar PATCH /dados para atualizar apenas os campos do packing list
      // O backend recalcula o rateio automaticamente se houver produtos vinculados
      const payload = {
        quantidadeVolumes: Number(packItem.quantidadeVolumes),
        lstTipoVolume: packItem.lstTipoVolume || 'BUNDLES',
        pesoLiquidoTotalKg: Number(packItem.pesoLiquidoTotalKg),
        pesoBrutoTotalKg: Number(packItem.pesoBrutoTotalKg),
        volumeCBMTotal: packItem.volumeCBMTotal ? Number(packItem.volumeCBMTotal) : null,
        descricao: packItem.descricao || '',
        flagPreenchimentoConcluido: !!packItem.flagPreenchimentoConcluido
      }
      response = await api.patch(`/EmbarquePackingLists/${packItem.idEmbarquePackingList}/dados`, payload)
    } else {
      // NOVO REGISTRO: Usar POST com payload completo incluindo produtos
      const payload = {
        idEmbarque: item.value.idEmbarque,
        idEmbarqueContainer: null,
        quantidadeVolumes: Number(packItem.quantidadeVolumes),
        lstTipoVolume: packItem.lstTipoVolume || 'BUNDLES',
        pesoLiquidoTotalKg: Number(packItem.pesoLiquidoTotalKg),
        pesoBrutoTotalKg: Number(packItem.pesoBrutoTotalKg),
        volumeCBMTotal: packItem.volumeCBMTotal ? Number(packItem.volumeCBMTotal) : null,
        aiGenerated: packItem.aiGenerated || false,
        descricao: packItem.descricao || '',
        flagPreenchimentoConcluido: !!packItem.flagPreenchimentoConcluido,
        produtos: packItem.idEmbarqueItem
          ? [{
              idEmbarquePackingListProduto: packItem.idEmbarquePackingListProduto || 0,
              idEmbarqueItem: packItem.idEmbarqueItem,
              idProduto: packItem.idProduto,
              quantidadeEmbarcadaUnBasica: packItem.quantidadeEmbarcadaUnBasica,
              quantidadeEmbarcadaUnFornecedor: packItem.quantidadeEmbarcadaUnFornecedor,
              lstUnidadeMedidaBasica: packItem.lstUnidadeMedidaBasica,
              lstUnidadeMedidaFornecedor: packItem.lstUnidadeMedidaFornecedor,
              fatorConversao: packItem.fatorConversao,
              pesoLiquidoKg: packItem.pesoLiquidoTotalKg || 0,
              pesoBrutoKg: packItem.pesoBrutoTotalKg || 0,
              volumeCBM: packItem.volumeCBMTotal || null,
              pesoDocumentalReplicado: packItem.pesoDocumentalReplicado || false
            }]
          : []
      }
      response = await api.post('/EmbarquePackingLists', payload)
    }

    // Capturar o objeto salvo retornado pela API
    const savedPackItem = response.data

    emit('show-notification', { message: 'Item do packing list salvo com sucesso!', type: 'success' })

    // Atualizar o item na lista com os dados retornados da API (incluindo produtos com valores rateados)
    const index = packingListItems.value.findIndex(p =>
      (packItem._tempId && p._tempId === packItem._tempId) ||
      (p.idEmbarquePackingList && p.idEmbarquePackingList === savedPackItem.idEmbarquePackingList)
    )
    if (index >= 0) {
      // Atualiza apenas os campos necessários, preservando a referência do objeto
      const itemAtual = packingListItems.value[index]
      itemAtual.idEmbarquePackingList = savedPackItem.idEmbarquePackingList
      itemAtual.idEmbarqueContainer = savedPackItem.idEmbarqueContainer
      itemAtual.quantidadeVolumes = savedPackItem.quantidadeVolumes
      itemAtual.lstTipoVolume = savedPackItem.lstTipoVolume
      itemAtual.pesoLiquidoTotalKg = savedPackItem.pesoLiquidoTotalKg
      itemAtual.pesoBrutoTotalKg = savedPackItem.pesoBrutoTotalKg
      itemAtual.volumeCBMTotal = savedPackItem.volumeCBMTotal
      itemAtual.descricao = savedPackItem.descricao
      itemAtual.flagPreenchimentoConcluido = savedPackItem.flagPreenchimentoConcluido
      itemAtual.aiGenerated = savedPackItem.aiGenerated
      itemAtual.tsCriacao = savedPackItem.tsCriacao
      itemAtual.tsModificacao = savedPackItem.tsModificacao
      // Atualizar produtos com valores rateados (se existirem)
      if (savedPackItem.produtos) {
        itemAtual.produtos = savedPackItem.produtos
      }
    }

    // Recarregar a lista para garantir que os rateios sejam refletidos em todos os lugares
    await carregarPackingList()
  } catch (e) {
    console.error('Erro ao salvar item do packing list:', e)
    emit('show-notification', { message: 'Erro ao salvar item do packing list.', type: 'error' })
  } finally {
    isLoadingPackingList.value = false
  }
}
const confirmarExclusaoPackingList = async (confirmado) => {
  if (!confirmado) {
    packingListConfirmDelete.value = null
    return
  }
  const item = packingListConfirmDelete.value
  packingListConfirmDelete.value = null

  // Se for um item não salvo (idEmbarquePackingList = 0), remove apenas localmente
  if (!item || !item.idEmbarquePackingList) {
    const idx = packingListItems.value.findIndex(p => item._tempId && p._tempId === item._tempId)
    if (idx >= 0) {
      packingListItems.value.splice(idx, 1)
    }
    emit('show-notification', { message: 'Item removido do packing list.', type: 'success' })
    return
  }

  try {
    await api.delete(`/EmbarquePackingLists/${item.idEmbarquePackingList}`)
    emit('show-notification', { message: 'Item do packing list excluído com sucesso!', type: 'success' })
    await carregarPackingList()
  } catch (e) {
    console.error('Erro ao excluir item do packing list:', e)
    emit('show-notification', { message: 'Erro ao excluir item do packing list.', type: 'error' })
  }
}

const handlePackingListSalvo = async () => {
  await carregarPackingList()
}

const handleContainersSalvo = async () => {
  await carregarContainers()
}

const handleTudoSalvo = async () => {
  await carregarPackingList()
  await carregarContainers()
}

// === CONTAINER CHANGE DETECTION ===
const CONTAINER_TRACKED_FIELDS = [
  'numeroContainer', 'lstTipoContainer', 'numeroLacre',
  'pesoTaraKg', 'vgmKg'
]

function containerTrackedFields (item) {
  const obj = {}
  for (const field of CONTAINER_TRACKED_FIELDS) {
    obj[field] = item[field] ?? ''
  }
  return JSON.stringify(obj)
}

function buildContainerKey (item) {
  return item.idEmbarqueContainer || item._tempId || ''
}

function storeContainerBaseline (item) {
  const key = buildContainerKey(item)
  if (key) {
    containerBaselineMap.value[key] = containerTrackedFields(item)
  }
}

function storeAllContainerBaselines () {
  containerBaselineMap.value = {}
  for (const item of containerItems.value) {
    storeContainerBaseline(item)
  }
}

function hasContainerChanges (item) {
  const key = buildContainerKey(item)
  const baseline = containerBaselineMap.value[key]
  // Container novo nunca salvo → sempre mostra ações
  if (!baseline && (!item.idEmbarqueContainer || item.idEmbarqueContainer === 0)) {
    return true
  }
  if (baseline) {
    return containerTrackedFields(item) !== baseline
  }
  return false
}

function hasAnyContainerChanges () {
  return containerItems.value.some(item => hasContainerChanges(item))
}

function cancelContainerRow (container) {
  const key = buildContainerKey(container)
  const baseline = containerBaselineMap.value[key]
  // Container novo nunca salvo → remover
  if (!baseline && (!container.idEmbarqueContainer || container.idEmbarqueContainer === 0)) {
    const idx = containerItems.value.findIndex(c => c._tempId === container._tempId)
    if (idx >= 0) containerItems.value.splice(idx, 1)
    return
  }
  // Container existente → restaurar baseline
  if (baseline) {
    const original = JSON.parse(baseline)
    Object.assign(container, original)
  }
}

function cancelAllContainers () {
  const toRemove = []
  containerItems.value.forEach((item, idx) => {
    const key = buildContainerKey(item)
    const hasBaseline = !!containerBaselineMap.value[key]
    if (!hasBaseline && (!item.idEmbarqueContainer || item.idEmbarqueContainer === 0)) {
      toRemove.push(idx)
    } else {
      cancelContainerRow(item)
    }
  })
  toRemove.sort((a, b) => b - a).forEach(idx => containerItems.value.splice(idx, 1))
}

// === CONTAINER METHODS ===
const carregarContainers = async () => {
  if (!item.value.idEmbarque) {
    containerItems.value = []
    storeAllContainerBaselines()
    return
  }
  isLoadingContainers.value = true
  try {
    const res = await api.get(`/EmbarqueContainers/embarque/${item.value.idEmbarque}`)
    containerItems.value = res.data || []
    storeAllContainerBaselines()
  } catch (e) {
    console.error('Erro ao carregar containers:', e)
    containerItems.value = []
    storeAllContainerBaselines()
  } finally {
    isLoadingContainers.value = false
  }
}

const adicionarLinhaContainer = () => {
  const tempId = Date.now() + Math.random()
  containerItems.value.push({
    _tempId: tempId,
    idEmbarqueContainer: 0,
    idEmbarque: item.value.idEmbarque,
    numeroContainer: '',
    lstTipoContainer: '',
    numeroLacre: '',
    pesoTaraKg: null,
    vgmKg: null,
    aiGenerated: false
  })
}

const salvarItemContainer = async (container) => {
  // Valida campos obrigatórios
  if (!container.numeroContainer || String(container.numeroContainer).trim() === '') {
    emit('show-notification', { message: 'O campo "Nº Container" é obrigatório.', type: 'warning' })
    return
  }
  // Valida campos numéricos obrigatórios (não força zero, notifica usuário)
  const camposObrigatorios = [
    { campo: 'pesoTaraKg', label: 'Tara (kg)' },
    { campo: 'vgmKg', label: 'VGM (kg)' }
  ]
  for (const { campo, label } of camposObrigatorios) {
    const val = container[campo]
    if (val === null || val === undefined || val === '' || Number(val) === 0) {
      emit('show-notification', {
        message: `O campo "${label}" é obrigatório. Preencha-o antes de salvar.`,
        type: 'warning'
      })
      return
    }
  }

  const payload = {
    idEmbarque: item.value.idEmbarque,
    numeroContainer: String(container.numeroContainer).trim(),
    lstTipoContainer: container.lstTipoContainer || null,
    numeroLacre: container.numeroLacre ? String(container.numeroLacre).trim() : null,
    pesoTaraKg: container.pesoTaraKg !== null && container.pesoTaraKg !== undefined && container.pesoTaraKg !== '' ? Number(container.pesoTaraKg) : null,
    vgmKg: container.vgmKg !== null && container.vgmKg !== undefined && container.vgmKg !== '' ? Number(container.vgmKg) : null,
    freeTimeDias: null,
    dataRetiradaPorto: null,
    dataDevolucaoVazio: null,
    aiGenerated: container.aiGenerated || false,
    aiDescription: null
  }

  try {
    isLoadingContainers.value = true
    let response
    if (container.idEmbarqueContainer > 0) {
      response = await api.put(`/EmbarqueContainers/${container.idEmbarqueContainer}`, payload)
    } else {
      response = await api.post('/EmbarqueContainers', payload)
    }

    // Capturar o objeto salvo retornado pela API
    const savedContainer = response.data

    emit('show-notification', { message: 'Container salvo com sucesso!', type: 'success' })

    // Atualizar APENAS o idEmbarqueContainer do item salvo (para novos registros)
    // sem sobrescrever os outros dados do formulário (preserva camelCase do frontend)
    const index = containerItems.value.findIndex(c =>
      (container._tempId && c._tempId === container._tempId) ||
      (c.idEmbarqueContainer && c.idEmbarqueContainer === savedContainer.idEmbarqueContainer)
    )
    if (index >= 0) {
      // Apenas atualiza o ID para que futuros salvamentos sejam PUT (edição)
      // Não sobrescreve os outros campos para evitar conflito PascalCase vs camelCase
      containerItems.value[index].idEmbarqueContainer = savedContainer.idEmbarqueContainer
    }
    // Atualizar baseline para detectar futuras alterações
    storeContainerBaseline(container)
    // NÃO chamar carregarContainers() - preserva edições não salvas das outras linhas
  } catch (e) {
    console.error('Erro ao salvar container:', e)
    emit('show-notification', { message: 'Erro ao salvar container.', type: 'error' })
  } finally {
    isLoadingContainers.value = false
  }
}

const confirmarExclusaoContainer = async (confirmado) => {
  if (!confirmado) {
    containerConfirmDelete.value = null
    return
  }
  const itemDel = containerConfirmDelete.value
  containerConfirmDelete.value = null

  // Verificar se o container possui packing lists associados
  if (itemDel && itemDel.conteudo?.length > 0) {
    emit('show-notification', {
      message: 'Não é possível excluir este container pois ele já possui packing lists associados. Remova os lotes do container na aba Distribuição de Carga primeiro.',
      type: 'error'
    })
    return
  }

  // Se for um item não salvo (idEmbarqueContainer = 0), remove apenas localmente
  if (!itemDel || !itemDel.idEmbarqueContainer) {
    const idx = containerItems.value.findIndex(c => itemDel._tempId && c._tempId === itemDel._tempId)
    if (idx >= 0) {
      containerItems.value.splice(idx, 1)
    }
    emit('show-notification', { message: 'Container removido da lista.', type: 'success' })
    return
  }

  try {
    await api.delete(`/EmbarqueContainers/${itemDel.idEmbarqueContainer}`)
    emit('show-notification', { message: 'Container excluído com sucesso!', type: 'success' })
    await carregarContainers()
  } catch (e) {
    console.error('Erro ao excluir container:', e)
    emit('show-notification', { message: 'Erro ao excluir container.', type: 'error' })
  }
}

const duplicarLinhaContainer = (container) => {
  const tempId = Date.now() + Math.random()
  containerItems.value.push({
    _tempId: tempId,
    idEmbarqueContainer: 0,
    idEmbarque: item.value.idEmbarque,
    numeroContainer: container.numeroContainer || '',
    lstTipoContainer: container.lstTipoContainer || '',
    numeroLacre: '',
    pesoTaraKg: null,
    vgmKg: null,
    aiGenerated: false
  })
}

const duplicarLinhaPackingList = (packItem) => {
  const tempId = Date.now() + Math.random()
  packingListItems.value.push({
    _tempId: tempId,
    idEmbarquePackingList: 0,
    idEmbarque: item.value.idEmbarque,
    idEmbarqueContainer: null,
    quantidadeVolumes: null,
    lstTipoVolume: packItem.lstTipoVolume || 'BUNDLES',
    pesoLiquidoTotalKg: null,
    pesoBrutoTotalKg: null,
    volumeCBMTotal: null,
    aiGenerated: false,
    descricao: '',
    flagPreenchimentoConcluido: false
  })
}

const _salvarTudoPackingList = async () => {
  if (isLoadingPackingList.value) return

  // Separar itens novos (idEmbarquePackingList = 0) dos existentes
  const itensNovos = packingListItems.value.filter(p => !p.idEmbarquePackingList || p.idEmbarquePackingList === 0)
  const itensExistentes = packingListItems.value.filter(p => p.idEmbarquePackingList > 0 && !p.flagPreenchimentoConcluido)

  // Verificar se há itens para salvar
  if (itensNovos.length === 0 && itensExistentes.length === 0) {
    emit('show-notification', { message: 'Nenhuma alteração pendente para salvar.', type: 'info' })
    return
  }

  let sucessos = 0
  let erros = 0

  isLoadingPackingList.value = true

  try {
    // Salvar itens novos (POST)
    for (const packItem of itensNovos) {
      try {
        // Validações básicas
        if (!packItem.quantidadeVolumes || Number(packItem.quantidadeVolumes) === 0) {
          emit('show-notification', { message: 'Quantidade de volumes é obrigatória para novo item.', type: 'warning' })
          erros++
          continue
        }
        if (!packItem.pesoLiquidoTotalKg || Number(packItem.pesoLiquidoTotalKg) === 0) {
          emit('show-notification', { message: 'Peso líquido é obrigatório para novo item.', type: 'warning' })
          erros++
          continue
        }
        if (!packItem.pesoBrutoTotalKg || Number(packItem.pesoBrutoTotalKg) === 0) {
          emit('show-notification', { message: 'Peso bruto é obrigatório para novo item.', type: 'warning' })
          erros++
          continue
        }

        const payload = {
          idEmbarque: item.value.idEmbarque,
          idEmbarqueContainer: null,
          quantidadeVolumes: Number(packItem.quantidadeVolumes),
          lstTipoVolume: packItem.lstTipoVolume || 'BUNDLES',
          pesoLiquidoTotalKg: Number(packItem.pesoLiquidoTotalKg),
          pesoBrutoTotalKg: Number(packItem.pesoBrutoTotalKg),
          volumeCBMTotal: packItem.volumeCBMTotal ? Number(packItem.volumeCBMTotal) : null,
          aiGenerated: packItem.aiGenerated || false,
          descricao: packItem.descricao || '',
          flagPreenchimentoConcluido: !!packItem.flagPreenchimentoConcluido,
          produtos: []
        }

        await api.post('/EmbarquePackingLists', payload)
        sucessos++
      } catch (e) {
        console.error('Erro ao salvar item novo:', e)
        erros++
      }
    }

    // Atualizar itens existentes (PATCH /dados)
    for (const packItem of itensExistentes) {
      try {
        const payload = {
          quantidadeVolumes: Number(packItem.quantidadeVolumes),
          lstTipoVolume: packItem.lstTipoVolume || 'BUNDLES',
          pesoLiquidoTotalKg: Number(packItem.pesoLiquidoTotalKg),
          pesoBrutoTotalKg: Number(packItem.pesoBrutoTotalKg),
          volumeCBMTotal: packItem.volumeCBMTotal ? Number(packItem.volumeCBMTotal) : null,
          descricao: packItem.descricao || '',
          flagPreenchimentoConcluido: !!packItem.flagPreenchimentoConcluido
        }

        await api.patch(`/EmbarquePackingLists/${packItem.idEmbarquePackingList}/dados`, payload)
        sucessos++
      } catch (e) {
        console.error('Erro ao atualizar item existente:', e)
        erros++
      }
    }

    // Recarregar lista para obter IDs dos novos itens e dados atualizados (incluindo rateios)
    await carregarPackingList()

    // Notificar resultado
    if (erros === 0) {
      emit('show-notification', {
        message: `Todos os itens do packing list foram salvos com sucesso! (${sucessos} itens)`,
        type: 'success'
      })
    } else {
      emit('show-notification', {
        message: `Salvamento parcial: ${sucessos} sucessos, ${erros} erros. Verifique os dados e tente novamente.`,
        type: 'warning'
      })
    }
  } catch (e) {
    console.error('Erro ao salvar tudo:', e)
    emit('show-notification', { message: 'Erro ao salvar itens do packing list.', type: 'error' })
  } finally {
    isLoadingPackingList.value = false
  }
}

const _salvarTudoContainers = async () => {
  if (isLoadingContainers.value) return

  // Separar containers novos (idEmbarqueContainer = 0) dos existentes
  const containersNovos = containerItems.value.filter(c => !c.idEmbarqueContainer || c.idEmbarqueContainer === 0)
  const containersExistentes = containerItems.value.filter(c => c.idEmbarqueContainer > 0 && !c.conteudo?.length > 0)

  // Verificar se há containers para salvar
  if (containersNovos.length === 0 && containersExistentes.length === 0) {
    emit('show-notification', { message: 'Nenhuma alteração pendente para salvar.', type: 'info' })
    return
  }

  let sucessos = 0
  let erros = 0

  isLoadingContainers.value = true

  try {
    // Salvar containers novos (POST)
    for (const container of containersNovos) {
      try {
        // Validações básicas
        if (!container.numeroContainer || String(container.numeroContainer).trim() === '') {
          emit('show-notification', { message: 'Nº Container é obrigatório para novo container.', type: 'warning' })
          erros++
          continue
        }
        if (!container.pesoTaraKg || Number(container.pesoTaraKg) === 0) {
          emit('show-notification', { message: 'Tara (kg) é obrigatória para novo container.', type: 'warning' })
          erros++
          continue
        }
        if (!container.vgmKg || Number(container.vgmKg) === 0) {
          emit('show-notification', { message: 'VGM (kg) é obrigatório para novo container.', type: 'warning' })
          erros++
          continue
        }

        const payload = {
          idEmbarque: item.value.idEmbarque,
          numeroContainer: String(container.numeroContainer).trim(),
          lstTipoContainer: container.lstTipoContainer || null,
          numeroLacre: container.numeroLacre ? String(container.numeroLacre).trim() : null,
          pesoTaraKg: Number(container.pesoTaraKg),
          vgmKg: Number(container.vgmKg),
          freeTimeDias: null,
          dataRetiradaPorto: null,
          dataDevolucaoVazio: null,
          aiGenerated: container.aiGenerated || false,
          aiDescription: null
        }

        await api.post('/EmbarqueContainers', payload)
        sucessos++
      } catch (e) {
        console.error('Erro ao salvar container novo:', e)
        erros++
      }
    }

    // Atualizar containers existentes (PUT)
    for (const container of containersExistentes) {
      try {
        const payload = {
          idEmbarque: item.value.idEmbarque,
          numeroContainer: String(container.numeroContainer).trim(),
          lstTipoContainer: container.lstTipoContainer || null,
          numeroLacre: container.numeroLacre ? String(container.numeroLacre).trim() : null,
          pesoTaraKg: Number(container.pesoTaraKg),
          vgmKg: Number(container.vgmKg),
          freeTimeDias: null,
          dataRetiradaPorto: null,
          dataDevolucaoVazio: null,
          aiGenerated: container.aiGenerated || false,
          aiDescription: null
        }

        await api.put(`/EmbarqueContainers/${container.idEmbarqueContainer}`, payload)
        sucessos++
      } catch (e) {
        console.error('Erro ao atualizar container existente:', e)
        erros++
      }
    }

    // Recarregar lista para obter IDs dos novos containers e dados atualizados
    await carregarContainers()

    // Notificar resultado
    if (erros === 0) {
      emit('show-notification', {
        message: `Todos os containers foram salvos com sucesso! (${sucessos} containers)`,
        type: 'success'
      })
    } else {
      emit('show-notification', {
        message: `Salvamento parcial: ${sucessos} sucessos, ${erros} erros. Verifique os dados e tente novamente.`,
        type: 'warning'
      })
    }
  } catch (e) {
    console.error('Erro ao salvar tudo:', e)
    emit('show-notification', { message: 'Erro ao salvar containers.', type: 'error' })
  } finally {
    isLoadingContainers.value = false
  }
}

// === HANDLERS DO MODO SMART (DRAG & DROP) ===
const handleVincularProduto = async ({ idPackingList, produto }) => {
  try {
    await api.post(`/EmbarquePackingLists/${idPackingList}/vincular-produto`, produto)
    emit('show-notification', { message: 'Produto vinculado ao lote com sucesso!', type: 'success' })
    await carregarPackingList()
  } catch (e) {
    console.error('Erro ao vincular produto:', e)
    emit('show-notification', { message: 'Erro ao vincular produto ao lote.', type: 'error' })
  }
}

const handleVincularContainer = async ({ idPackingList, idContainer }) => {
  try {
    await api.patch(`/EmbarquePackingLists/${idPackingList}/vincular-container`, { idEmbarqueContainer: idContainer })
    emit('show-notification', { message: 'Lote vinculado ao container com sucesso!', type: 'success' })
    await carregarPackingList()
    await carregarContainers()
  } catch (e) {
    console.error('Erro ao vincular container:', e)
    const msg = e.response?.data?.message || 'Erro ao vincular lote ao container.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const handleDesvincularContainer = async (idPackingList) => {
  try {
    await api.patch(`/EmbarquePackingLists/${idPackingList}/desvincular-container`)
    emit('show-notification', { message: 'Lote desvinculado do container.', type: 'success' })
    await carregarPackingList()
    await carregarContainers()
  } catch (e) {
    console.error('Erro ao desvincular container:', e)
    emit('show-notification', { message: 'Erro ao desvincular lote do container.', type: 'error' })
  }
}

const handleDesvincularProduto = async ({ idPackingList, idEmbarquePackingListProduto }) => {
  try {
    await api.delete(`/EmbarquePackingLists/${idPackingList}/produto/${idEmbarquePackingListProduto}`)
    emit('show-notification', { message: 'Produto desalocado do lote com sucesso!', type: 'success' })
    await carregarPackingList()
  } catch (e) {
    console.error('Erro ao desvincular produto:', e)
    emit('show-notification', { message: 'Erro ao desalocar produto do lote.', type: 'error' })
  }
}

const handleToggleFlagLote = async ({ idPackingList, flagPreenchimentoConcluido }) => {
  try {
    await api.patch(`/EmbarquePackingLists/${idPackingList}/flag-lote`, { flagPreenchimentoConcluido })
    emit('show-notification', {
      message: flagPreenchimentoConcluido ? 'Lote marcado como preenchido.' : 'Lote marcado como pendente.',
      type: 'success'
    })
    await carregarPackingList()
  } catch (e) {
    console.error('Erro ao alterar FlagPreenchimentoConcluido:', e)
    emit('show-notification', { message: 'Erro ao alterar status do lote.', type: 'error' })
  }
}

const parceirosMap = computed(() => {
  const map = {}
  // Extrair fornecedores dos itens do embarque usando idFornecedorDocumento
  // O nome já vem do backend via nomeFornecedor, mas mantemos o map como fallback
  for (const item of itensEmbarque.value) {
    if (item.idFornecedorDocumento && !map[item.idFornecedorDocumento]) {
      map[item.idFornecedorDocumento] = item.nomeFornecedor || `Fornecedor #${item.idFornecedorDocumento}`
    }
  }
  // Incluir transportador do embarque no map de parceiros
  if (item.value?.idParceiroNegocioTransportador && !map[item.value.idParceiroNegocioTransportador]) {
    map[item.value.idParceiroNegocioTransportador] = item.value.nomeTransportador || `Transportador #${item.value.idParceiroNegocioTransportador}`
  }
  return map
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas([
      'Embarque',
      'Embarque',
      'ContratoCompra',
      'ContratoProduto',
      'EmbarqueFatura',
      'EmbarqueFaturas',
      'EmbarqueItem',
      'EmbarqueItems',
      'EmbarquePackingList',
      'EmbarqueContainer',
      'Produto',
      'Produto'
    ])
    Object.assign(listas, result)
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares:', e)
    emit('show-notification', { message: 'Erro ao carregar listas auxiliares.', type: 'error' })
  }
}

const toggleAccordion = (key) => {
  accordionState[key] = !accordionState[key]
}

const handlePipelineClick = (etapa) => {
  // Se clicou na etapa "embarque" e já estamos nela, recarrega o grid de embarques
  if (etapa.key === 'embarque' && props.pipelineProps?.etapaAtiva === 'embarque') {
    carregarEmbarquesCota()
    return
  }
  // Caso contrário, propaga normalmente para o layout
  const action = () => {
    emit('pipeline-navegar', etapa.key)
  }
  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value.show()
  } else {
    action()
  }
}

const handleCancel = () => {
  const action = () => {
    if (props.drilldownMode) {
      emit('drilldown-pop')
    } else {
      emit('cancelar')
    }
  }
  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value.show()
  } else {
    action()
  }
}

const _selecionarEmbarque = async (emb) => {
  if (emb.idEmbarque === item.value.idEmbarque) return

  const action = async () => {
    // GUARDIÃO: Se já estamos no modo drilldown de embarque, apenas atualizamos o item atual
    // em vez de empilhar um novo nível (que adicionaria uma barra vertical extra).
    // Mesma lógica aplicada em ContratoCompraCotasForm.vue:selecionarCota
    if (props.drilldownMode && props.pipelineProps?.etapaAtiva === 'embarque') {
      console.log('[EmbarquesForm] Embarque já ativo no drilldown. Atualizando formulário localmente em vez de empilhar.', emb.idEmbarque)
      initializeForm(emb)
      await carregarFaturas()
      await carregarItensEmbarque()
      emit('itemAtualizado', emb)
      return
    }

    console.log('[EmbarquesForm] Emitindo drilldown-push para embarque', emb.idEmbarque)
    emit('drilldown-push', {
      type: 'embarque',
      id: emb.idEmbarque,
      props: {
        itemParaEditar: emb,
        contextoPai: props.contextoPai
      }
    })
  }

  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value.show()
  } else {
    action()
  }
}

const _iniciarNovoEmbarque = () => {
  const action = () => {
    initializeForm(props.modeloVazio)
    if (isFromQuota.value) {
      item.value.lstTipoOperacaoComex = 'COMEX_TP_OPER1'
      item.value.lstIncoterm = props.contextoPai.lstIncoterm || ''
      item.value.lstLocalIncoterm = props.contextoPai.lstLocalIncoterm || ''
      item.value.idPurchaseOrder = props.contextoPai.idPurchaseOrder
      initializeForm(item.value)
    } else if (props.contextoPai?.tipoOperacao) {
      item.value.lstTipoOperacaoComex = props.contextoPai.tipoOperacao
      initializeForm(item.value)
    }
    faturasEmbarque.value = []
    faturaSelecionada.value = null
    itensEmbarque.value = []
    emit('itemAtualizado', item.value)
  }

  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value.show()
  } else {
    action()
  }
}

const validarRegrasDeNegocio = async () => {
  const labelsObrigatorios = document.querySelectorAll('label.is-required')
  let hasBaseErrors = false

  for (const key in errors.value) {
    delete errors.value[key]
  }

  labelsObrigatorios.forEach(label => {
    if (label.offsetParent === null) return
    const inputId = label.getAttribute('for')
    if (!inputId) return
    const inputElement = document.getElementById(inputId)
    if (!inputElement) return

    let isEmpty = false
    if (inputElement.tagName === 'SELECT') {
      isEmpty = !inputElement.value || inputElement.value === ''
    } else {
      isEmpty = !inputElement.value || String(inputElement.value).trim() === ''
    }

    if (isEmpty) {
      const nomeCampoLimpo = label.innerText.replace('*', '').trim()
      errors.value[inputId] = `O campo "${nomeCampoLimpo}" é de preenchimento obrigatório.`
      hasBaseErrors = true
    }
  })

  if (hasBaseErrors) {
    emit('show-notification', { message: 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.', type: 'warning' })
    if (focarPrimeiroErro) await focarPrimeiroErro()
    return false
  }

  return true
}

const salvar = async () => {
  if (!podeEditar.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = { ...item.value }

  if (!payload.idEmbarque) {
    payload.lstStatusEmbarque = 'LOG_ST_EMBARQUE1'
  }

  try {
    isLoading.value = true
    const res = await api[payload.idEmbarque ? 'put' : 'post'](
      payload.idEmbarque ? `/Embarques/${payload.idEmbarque}` : '/Embarques',
      payload
    )

    const embarqueSalvo = res.data
    item.value.idEmbarque = embarqueSalvo.idEmbarque

    // Após salvar, carrega os dados relacionados (itens, packing list, faturas)
    await carregarFaturas()
    await carregarItensEmbarque()
    await carregarPackingList()

    emit('show-notification', { message: 'Registro salvo com sucesso!', type: 'success' })
    emit('itemAtualizado', embarqueSalvo)
  } catch (e) {
    console.error('Erro ao salvar:', e)
    emit('show-notification', { message: 'Erro ao salvar o registro.', type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const salvarAlteracoes = async () => {
  if (!podeEditar.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = { ...item.value }

  if (!payload.idEmbarque) {
    payload.lstStatusEmbarque = 'LOG_ST_EMBARQUE1'
  }

  try {
    isLoading.value = true
    const res = await api[payload.idEmbarque ? 'put' : 'post'](
      payload.idEmbarque ? `/Embarques/${payload.idEmbarque}` : '/Embarques',
      payload
    )

    const embarqueSalvo = res.data
    item.value.idEmbarque = embarqueSalvo.idEmbarque

    // Após salvar, carrega os dados relacionados (itens, packing list, faturas)
    await carregarFaturas()
    await carregarItensEmbarque()
    await carregarPackingList()

    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
    // NÃO emite itemAtualizado — o formulário permanece aberto
  } catch (e) {
    console.error('Erro ao salvar alterações:', e)
    emit('show-notification', { message: 'Erro ao salvar alterações.', type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const iniciarTransito = async () => {
  if (!isEmbarqueSaved.value || !podeEditar.value || isLoading.value) return

  // Mapa de campo → accordion/frame dentro da aba Dados Logísticos
  const campoParaAccordion = {
    lstTipoOperacaoComex: 'dadosBasicos',
    idFilial: 'dadosBasicos',
    idLocalEstoqueDestino: 'dadosBasicos',
    lstTipoDocumentoTransporte: 'dadosBasicos',
    numeroDocumentoTransporte: 'dadosBasicos',
    lstModalTransporte: 'roteamento',
    lstPaisOrigem: 'roteamento',
    lstPortoOrigem: 'roteamento',
    lstPaisDestino: 'roteamento',
    lstPortoDestino: 'roteamento',
    idParceiroNegocioTransportador: 'agentes',
    nomeVeiculoTransporte: 'agentes',
    numeroViagem: 'agentes',
    lstIncoterm: 'incoterm',
    lstLocalIncoterm: 'incoterm',
    dataColetaNoFornecedor: 'prazos',
    dataEmissaoDocumentoTransporte: 'prazos',
    dataEmbarqueETD: 'prazos',
    dataChegadaETA: 'prazos'
  }

  // 1. Validar TODOS os campos obrigatórios (sem early return)
  const campos = [
    { id: 'lstTipoOperacaoComex', label: 'Tipo de Operação', ref: 'lstTipoOperacaoComex' },
    { id: 'idFilial', label: 'Filial', ref: 'idFilial' },
    { id: 'idLocalEstoqueDestino', label: 'Local Estoque (Destino)', ref: 'idLocalEstoqueDestino' },
    { id: 'lstModalTransporte', label: 'Modal de Transporte', ref: 'lstModalTransporte' },
    { id: 'lstPaisOrigem', label: 'País de Origem', ref: 'lstPaisOrigem' },
    { id: 'lstPortoOrigem', label: 'Porto de Origem', ref: 'lstPortoOrigem' },
    { id: 'lstPaisDestino', label: 'País de Destino', ref: 'lstPaisDestino' },
    { id: 'lstPortoDestino', label: 'Porto de Destino', ref: 'lstPortoDestino' },
    { id: 'idParceiroNegocioTransportador', label: 'Transportador', ref: 'idParceiroNegocioTransportador' },
    { id: 'nomeVeiculoTransporte', label: 'Nome do Veículo', ref: 'nomeVeiculoTransporte' },
    { id: 'numeroViagem', label: 'Nº da Viagem', ref: 'numeroViagem' },
    { id: 'lstIncoterm', label: 'Incoterm', ref: 'lstIncoterm' },
    { id: 'lstLocalIncoterm', label: 'Local do Incoterm', ref: 'lstLocalIncoterm' },
    { id: 'lstTipoDocumentoTransporte', label: 'Tipo Doc. Transporte', ref: 'lstTipoDocumentoTransporte' },
    { id: 'numeroDocumentoTransporte', label: 'Nº Doc. Transporte', ref: 'numeroDocumentoTransporte' },
    { id: 'dataColetaNoFornecedor', label: 'Data Coleta no Fornecedor', ref: 'dataColetaNoFornecedor' },
    { id: 'dataEmissaoDocumentoTransporte', label: 'Data Emissão Doc. Transporte', ref: 'dataEmissaoDocumentoTransporte' },
    { id: 'dataEmbarqueETD', label: 'Data de Embarque (ETD)', ref: 'dataEmbarqueETD' },
    { id: 'dataChegadaETA', label: 'Data de Chegada (ETA)', ref: 'dataChegadaETA' }
  ]

  // FASE 1: Percorre TODOS os campos, marca TODOS os erros
  const camposVazios = []
  for (const campo of campos) {
    const valor = item.value[campo.ref]
    const isEmpty = valor === null || valor === undefined || valor === '' || valor === 0
    if (isEmpty) {
      errors.value[campo.id] = `O campo "${campo.label}" é obrigatório para iniciar o trânsito.`
      camposVazios.push(campo)
    }
  }

  // FASE 2: Se houver campos vazios, navega e foca no primeiro
  if (camposVazios.length > 0) {
    // Navega para a aba Dados Logísticos
    abaAtiva.value = 'dadosLogisticos'

    // Abre o accordion/frame do primeiro campo pendente
    const primeiroCampo = camposVazios[0]
    const accordionKey = campoParaAccordion[primeiroCampo.id]
    if (accordionKey) {
      accordionState[accordionKey] = true
    }

    // Aguarda re-renderização do Vue e foca no campo
    await nextTick()
    const el = document.getElementById(primeiroCampo.id)
    if (el) {
      el.focus()
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    emit('show-notification', {
      message: `Preencha o campo "${primeiroCampo.label}" antes de iniciar o trânsito.`,
      type: 'warning'
    })
    return
  }

  // 2. Validar itens do embarque
  if (itensEmbarque.value.length === 0) {
    emit('show-notification', {
      message: 'O embarque deve ter pelo menos 1 produto alocado antes de iniciar o trânsito.',
      type: 'warning'
    })
    return
  }

  // 3. Validar packing list concluído
  const packingConcluido = packingListItems.value.some(pl => pl.flagPreenchimentoConcluido)
  if (!packingConcluido) {
    emit('show-notification', {
      message: 'O embarque deve ter pelo menos 1 packing list com preenchimento concluído.',
      type: 'warning'
    })
    return
  }

  // 4. Validar containers
  if (containerItems.value.length === 0) {
    emit('show-notification', {
      message: 'O embarque deve ter pelo menos 1 container cadastrado.',
      type: 'warning'
    })
    return
  }

  // 5. Validar alocação completa
  const naoAlocados = packingListItems.value.filter(pl => !pl.idEmbarqueContainer)
  if (naoAlocados.length > 0) {
    emit('show-notification', {
      message: `Existem ${naoAlocados.length} packing list(s) não alocados em containers. Todos devem estar alocados antes de iniciar o trânsito.`,
      type: 'warning'
    })
    return
  }

  // 6. Salvar dados do formulário primeiro (PUT) para persistir no banco
  //    antes de alterar o status. O backend recebe o DTO completo e persiste
  //    os campos obrigatórios antes da transição de status.
  try {
    isLoading.value = true
    const payload = { ...item.value }
    await api.put(`/Embarques/${item.value.idEmbarque}`, payload)

    // 7. Chamar API de transição de status
    const res = await api.post(`/Embarques/${item.value.idEmbarque}/iniciar-transito`)

    // 8. Atualizar item com retorno do backend (inclui novo status)
    if (res.data) {
      Object.assign(item.value, res.data)
    }

    emit('show-notification', {
      message: 'Trânsito iniciado com sucesso! O embarque agora está em trânsito.',
      type: 'success'
    })

    // Recarregar dados
    await carregarFaturas()
    await carregarItensEmbarque()
    await carregarPackingList()
    await carregarContainers()
    emit('itemAtualizado', item.value)
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao iniciar trânsito do embarque.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const realizarAtracacao = async () => {
  if (!isEmbarqueSaved.value || !podeEditar.value || isLoading.value) return

  try {
    isLoading.value = true

    // 1. Salvar dados do formulário primeiro (PUT) para persistir no banco
    const payload = { ...item.value }
    await api.put(`/Embarques/${item.value.idEmbarque}`, payload)

    // 2. Chamar API de transição de status
    const res = await api.post(`/Embarques/${item.value.idEmbarque}/realizar-atracacao`)

    // 3. Atualizar item com retorno do backend (inclui novo status)
    if (res.data) {
      Object.assign(item.value, res.data)
    }

    emit('show-notification', {
      message: 'Atracação realizada com sucesso! O embarque agora está atracado.',
      type: 'success'
    })

    await carregarFaturas()
    await carregarItensEmbarque()
    await carregarPackingList()
    await carregarContainers()
    emit('itemAtualizado', item.value)
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao realizar atracação do embarque.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const retornarEtapaAnterior = async () => {
  if (!item.value.idEmbarque || !podeEditar.value || isLoading.value) return
  if (item.value.lstStatusEmbarque !== LOG_ST_EMBARQUE.Preembarque) return

  try {
    isLoading.value = true
    const res = await api.post(`/Embarques/${item.value.idEmbarque}/retornar-etapa-anterior`)
    if (res.data) {
      Object.assign(item.value, res.data)
      // Garantir que lstStatusEmbarque seja propagado mesmo se o DTO tiver nome diferente
      if (res.data.lstStatusEmbarque) {
        item.value.lstStatusEmbarque = res.data.lstStatusEmbarque
      }
    }
    emit('show-notification', {
      message: 'Embarque retornado para a etapa anterior com sucesso.',
      type: 'success'
    })
    await carregarFaturas()
    await carregarItensEmbarque()
    await carregarPackingList()
    await carregarContainers()
    // Primeiro navega para a nova etapa, depois atualiza o item
    // para que o novo componente monte com o status correto
    emit('pipeline-navegar', 'validacao')
    emit('itemAtualizado', item.value)
  } catch (e) {
    const data = e.response?.data
    const msg = typeof data === 'string' ? data : (data?.message || 'Erro ao retornar etapa anterior.')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

function getTextoLista (lista, id) {
  if (!lista) return id || '-'
  const found = lista.find(l => l.idListaClienteItem === id)
  return found ? found.textoLista : (id || '-')
}

const _obterCorStatus = (idStatus) => {
  const texto = String(getTextoLista(listas.LstStatusEmbarque, idStatus)).toUpperCase()
  if (texto.includes('CONCLUÍDO') || texto.includes('FINALIZADO') || texto.includes('ENTREGUE')) return 'badge-ghost-success'
  if (texto.includes('ABERTO') || texto.includes('PENDENTE') || texto.includes('TRÂNSITO')) return 'badge-ghost-warning'
  if (texto.includes('CANCELADO')) return 'badge-ghost-danger'
  if (texto.includes('ANÁLISE') || texto.includes('PLANEJADO')) return 'badge-ghost-info'
  return 'badge-ghost-secondary'
}

onMounted(async () => {
  await carregarListas()

  if (!item.value.idEmbarque) {
    if (isFromQuota.value) {
      item.value.lstTipoOperacaoComex = 'COMEX_TP_OPER1'
      item.value.lstIncoterm = props.contextoPai.lstIncoterm || ''
      item.value.lstLocalIncoterm = props.contextoPai.lstLocalIncoterm || ''
      item.value.idPurchaseOrder = props.contextoPai.idPurchaseOrder
    } else if (props.contextoPai?.tipoOperacao) {
      item.value.lstTipoOperacaoComex = props.contextoPai.tipoOperacao
    }
    // GUARDIÃO: Resetar baseline após preencher valores iniciais para não disparar hasChanges
    initializeForm(item.value)
  }

  if (item.value.idEmbarque) {
    await carregarFaturas()
    await carregarItensEmbarque()
    await carregarPackingList()
    await carregarContainers()
  }
  if (props.drilldownMode) {
    await carregarEmbarquesCota()
  }
  if (isFromQuota.value) {
    await carregarProdutosCota()
  }
  await carregarFornecedoresPermitidos()
})

watch(() => props.itemParaEditar, async (newVal) => {
  if (newVal) {
    item.value = { ...newVal }
    await carregarFaturas()
    await carregarItensEmbarque()
    await carregarPackingList()
    await carregarContainers()
    await carregarFornecedoresPermitidos()
  }
}, { deep: true })

// Limpa o lote de fabricação quando o fornecedor (parceiro produtor) é alterado ou limpo
watch(() => itemEmEdicao.value?.idParceiroNegocioProdutor, (novoFornecedor, antigoFornecedor) => {
  if (novoFornecedor !== antigoFornecedor && itemEmEdicao.value) {
    itemEmEdicao.value.idLoteFabricacao = null
    itemEmEdicao.value.numeroLoteFornecedor = ''
  }
})

</script>

<template>
  <div class="embarques-form-wrapper" v-bind="$attrs">
    <SemAcessoModulo v-if="!temModuloEmbarques" titulo="Embarques" />
    <template v-if="temModuloEmbarques">
    <FormularioCadastro
      module-name="Embarque"
      :is-loading="isLoading"
      :is-editing="isEditing"
      :entity-info="entityInfo"
      :status="getTextoLista(listas.LstStatusEmbarque, item?.lstStatusEmbarque)"
      :titulo-registro="item?.numeroProcessoEmbarque"
      :accordion-state="accordionState"
      :ocultar-salvar="!temModuloEmbarques || !podeEditar"
      @submit="salvar"
      @submit-continuar="salvarAlteracoes"
      @cancelar="handleCancel"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <template #info-header v-if="isEditing">
        <div class="info-item">
          <strong>Nº Doc. Transporte: </strong>
          <span>{{ getTextoLista(listas.LstTipoDocumentoTransporte, item?.lstTipoDocumentoTransporte) }} {{ item?.numeroDocumentoTransporte || '---' }}</span>
        </div>
        <div class="info-item">
          <strong>Operação Comex: </strong>
          <span>{{ getTextoLista(listas.LstTipoOperacaoComex, item?.lstTipoOperacaoComex) || '---' }}</span>
        </div>
      </template>
      <template #acoes-header v-if="isEditing">
        <button
          type="button"
          class="btn-icon-minimalista me-3"
          @click="mostrarAuditoria = true"
          title="Ver Histórico de Alterações"
        >
          <i class="bi bi-clock-history"></i>
        </button>
      </template>

      <ComexPipelineStatus
        v-if="drilldownMode"
        v-bind="pipelineProps"
        @navegar="handlePipelineClick"
      />

      <!-- BARRA DE AÇÕES DO EMBARQUE -->
      <!-- Oculta em modo read-only (etapa ultrapassada selecionada para visualização) -->
      <div v-if="drilldownMode && !pipelineProps?.readOnly" class="d-flex justify-content-between align-items-center gap-2 mb-3">
        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-warning fw-bold"
            @click="showModalDocumentosGrid = true"
          >
            <i class="bi bi-receipt me-1"></i> Lançar Despesas
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-info fw-bold"
            @click="showModalSolicitacaoAdiantamento = true"
          >
            <i class="bi bi-cash-coin me-1"></i> Solicitar Adiantamento
          </button>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger fw-bold"
            @click="handleCancel"
          >
            <i class="bi bi-x-lg me-1"></i> Cancelar Embarque
          </button>
          <!-- Botão Retornar Etapa Anterior: habilitado apenas se status for Pré-embarque -->
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary fw-bold"
            :disabled="item.lstStatusEmbarque !== LOG_ST_EMBARQUE.Preembarque || isLoading"
            @click="retornarEtapaAnterior"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-return-left me-1"></i>
            Retornar Etapa Anterior
          </button>
          <!-- Botão Trânsito Iniciado: visível apenas se status for Pré-embarque ou anterior -->
          <button
            v-if="item.lstStatusEmbarque === LOG_ST_EMBARQUE.Preembarque || !item.lstStatusEmbarque"
            type="button"
            class="btn btn-sm btn-outline-info fw-bold"
            @click="iniciarTransito"
            :disabled="!isEmbarqueSaved || !podeEditar || isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-truck me-1"></i>
            Trânsito Iniciado
          </button>
          <!-- Botão Atracação Realizada: visível apenas se status for Trânsito -->
          <button
            v-if="item.lstStatusEmbarque === LOG_ST_EMBARQUE.Transito"
            type="button"
            class="btn btn-sm btn-outline-success fw-bold"
            @click="realizarAtracacao"
            :disabled="!podeEditar || isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-anchor me-1"></i>
            Atracação Realizada
          </button>
        </div>
      </div>

      <!-- ABAS PRINCIPAIS DO EMBARQUE -->
      <ul class="nav nav-tabs nav-fill mb-3">
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: abaAtiva === 'dadosLogisticos' }"
            type="button"
            @click="abaAtiva = 'dadosLogisticos'"
          >
            <i class="bi bi-info-circle me-1"></i> Dados Logísticos
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: abaAtiva === 'pesosProdutos' }"
            type="button"
            @click="abaAtiva = 'pesosProdutos'"
          >
            <i class="bi bi-weight-scale me-1"></i> Pesos dos Produtos
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: abaAtiva === 'packingList' }"
            type="button"
            @click="abaAtiva = 'packingList'"
          >
            <i class="bi bi-list-check me-1"></i> Packing List
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: abaAtiva === 'containers' }"
            type="button"
            @click="abaAtiva = 'containers'"
          >
            <i class="bi bi-boxes me-1"></i> Containers
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: abaAtiva === 'distribuicao' }"
            type="button"
            @click="abaAtiva = 'distribuicao'"
          >
            <i class="bi bi-magic me-1"></i> Distribuição de Carga
          </button>
        </li>
        <li class="nav-item" v-if="item.lstStatusEmbarque === LOG_ST_EMBARQUE.Transito">
          <button
            class="nav-link"
            :class="{ active: abaAtiva === 'atracacao' }"
            type="button"
            @click="abaAtiva = 'atracacao'"
          >
            <i class="bi bi-anchor me-1"></i> Atracação
          </button>
        </li>
      </ul>

      <div class="tab-content">
        <!-- ABA 1: DADOS LOGÍSTICOS -->
        <div class="tab-pane fade" :class="{ show: abaAtiva === 'dadosLogisticos', active: abaAtiva === 'dadosLogisticos' }">
          <!-- FRAME 1: DADOS BÁSICOS E IDENTIFICADORES -->
          <div class="card mb-3 border-warning-subtle">
            <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('dadosBasicos')">
              <h5 class="mb-0 text-warning-emphasis fw-bold"><i class="bi bi-info-circle me-2"></i>Dados Básicos e Identificadores</h5>
              <i class="bi" :class="accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
            <div v-show="accordionState.dadosBasicos" class="card-body border-top border-warning-subtle">
              <div class="row g-3">
                <div class="col-md-3">
                  <CampoSelect
                    id="lstTipoOperacaoComex"
                    label="Tipo de Operação"
                    v-model="item.lstTipoOperacaoComex"
                    :options="listas.LstTipoOperacaoComex || []"
                    option-label="textoLista"
                    option-value="idListaClienteItem"
                    required
                    :disabled="!podeEditar || (isFromQuota && !isEditing)"
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="numeroProcessoEmbarque"
                    :label="labelProcessoInterno"
                    v-model="item.numeroProcessoEmbarque"
                    :required="isEditing"
                    disabled
                  />
                </div>
                <div class="col-md-2">
                  <CampoInput
                    id="numeroBooking"
                    label="Nº Booking"
                    v-model="item.numeroBooking"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-2">
                  <CampoBusca
                    id="idFilial"
                    label="Filial"
                    v-model="item.idFilial"
                    :configuracao-busca="configBuscaFilial"
                    required
                    disabled
                  />
                </div>
                <div class="col-md-2">
                  <CampoBusca
                    id="idLocalEstoqueDestino"
                    label="Local Estoque (Destino)"
                    v-model="item.idLocalEstoqueDestino"
                    :configuracao-busca="configBuscaLocalEstoque"
                    :disabled="!podeEditar"
                    @show-notification="(p) => emit('show-notification', p)"
                  />
                </div>
                <div class="col-md-3">
                  <CampoBuscaLista
                    id="lstTipoDocumentoTransporte"
                    label="Tipo Doc. Transporte"
                    v-model="item.lstTipoDocumentoTransporte"
                    tabela="Embarque"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="numeroDocumentoTransporte"
                    label="Nº Doc. Transporte"
                    v-model="item.numeroDocumentoTransporte"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- FRAME 2: ROTEAMENTO E LOGÍSTICA -->
          <div class="card mb-3 border-warning-subtle">
            <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('roteamento')">
              <h5 class="mb-0 text-warning-emphasis fw-bold"><i class="bi bi-geo-alt me-2"></i>Roteamento e Logística</h5>
              <i class="bi" :class="accordionState.roteamento ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
            <div v-show="accordionState.roteamento" class="card-body border-top border-warning-subtle">
              <div class="row g-3">
                <div class="col-md-4">
                  <CampoSelect
                    id="lstModalTransporte"
                    label="Modal de Transporte"
                    v-model="item.lstModalTransporte"
                    :options="listas.LstModalTransporte || []"
                    option-label="textoLista"
                    option-value="idListaClienteItem"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-4">
                  <CampoBuscaLista
                    id="lstPaisOrigem"
                    label="País de Origem"
                    v-model="item.lstPaisOrigem"
                    tabela="Embarque"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-4">
                  <CampoBuscaLista
                    id="lstPortoOrigem"
                    label="Porto/Aeroporto Origem"
                    v-model="item.lstPortoOrigem"
                    tabela="Embarque"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-4">
                  <CampoBuscaLista
                    id="lstPaisDestino"
                    label="País de Destino"
                    v-model="item.lstPaisDestino"
                    tabela="Embarque"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-4">
                  <CampoBuscaLista
                    id="lstPortoDestino"
                    label="Porto/Aeroporto Destino"
                    v-model="item.lstPortoDestino"
                    tabela="Embarque"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- FRAME 3: DETALHES DO VEÍCULO E AGENTES -->
          <div class="card mb-3 border-warning-subtle">
            <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('agentes')">
              <h5 class="mb-0 text-warning-emphasis fw-bold"><i class="bi bi-truck me-2"></i>Agentes e Veículo</h5>
              <i class="bi" :class="accordionState.agentes ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
            <div v-show="accordionState.agentes" class="card-body border-top border-warning-subtle">
              <div class="row g-3">
                <div class="col-md-6">
                  <CampoBusca
                    id="idParceiroNegocioTransportador"
                    label="Transportador (Armador/Cia Aérea)"
                    v-model="item.idParceiroNegocioTransportador"
                    :configuracao-busca="configBuscaTransportador"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-6">
                  <CampoBusca
                    id="idParceiroNegocioAgenteCarga"
                    label="Agente de Carga"
                    v-model="item.idParceiroNegocioAgenteCarga"
                    :configuracao-busca="configBuscaAgenteCarga"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-6">
                  <CampoInput
                    id="nomeVeiculoTransporte"
                    label="Nome do Veículo (Navio/Aeronave)"
                    v-model="item.nomeVeiculoTransporte"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-6">
                  <CampoInput
                    id="numeroViagem"
                    label="Nº da Viagem"
                    v-model="item.numeroViagem"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- FRAME 4: INCOTERM -->
          <div class="card mb-3 border-warning-subtle">
            <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('incoterm')">
              <h5 class="mb-0 text-warning-emphasis fw-bold"><i class="bi bi-currency-exchange me-2"></i>Incoterm</h5>
              <i class="bi" :class="accordionState.incoterm ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
            <div v-show="accordionState.incoterm" class="card-body border-top border-warning-subtle">
              <div class="row g-3">
                <div class="col-md-4">
                  <CampoBuscaLista
                    id="lstIncoterm"
                    label="Incoterm"
                    v-model="item.lstIncoterm"
                    tabela="Embarque"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-8">
                  <CampoBuscaLista
                    id="lstLocalIncoterm"
                    label="Local do Incoterm"
                    v-model="item.lstLocalIncoterm"
                    tabela="Embarque"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- FRAME 5: PRAZOS LOGÍSTICOS -->
          <div class="card mb-3 border-warning-subtle">
            <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('prazos')">
              <h5 class="mb-0 text-warning-emphasis fw-bold"><i class="bi bi-calendar-event me-2"></i>Datas Logísticas</h5>
              <i class="bi" :class="accordionState.prazos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
            <div v-show="accordionState.prazos" class="card-body border-top border-warning-subtle">
              <div class="row g-3">
                <div class="col-md-3">
                  <CampoInput
                    id="dataColetaNoFornecedor"
                    label="Data Coleta no Fornecedor"
                    v-model="item.dataColetaNoFornecedor"
                    type="date"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="dataEmissaoDocumentoTransporte"
                    :label="labelDataEmissaoDocumentoTransporte"
                    v-model="item.dataEmissaoDocumentoTransporte"
                    type="date"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="dataEmbarqueETD"
                    label="Data de Embarque (ETD)"
                    v-model="item.dataEmbarqueETD"
                    type="date"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="dataChegadaETA"
                    label="Data de Chegada (ETA)"
                    v-model="item.dataChegadaETA"
                    type="date"
                    :required="isPosPreembarque"
                    :disabled="!podeEditar"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ABA 2: PESOS DOS PRODUTOS -->
        <div class="tab-pane fade" :class="{ show: abaAtiva === 'pesosProdutos', active: abaAtiva === 'pesosProdutos' }">
          <div v-if="!isEmbarqueSaved" class="text-center py-5">
            <p class="text-muted mb-0">Salve o embarque primeiro para acessar os Pesos dos Produtos.</p>
          </div>

          <div v-else>
            <div class="card mb-3 border-warning-subtle">
              <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center py-2">
                <h6 class="mb-0 text-warning-emphasis fw-bold small">
                  <i class="bi bi-weight-scale me-1"></i> Pesos dos Produtos
                  <span v-if="itensEmbarque.length > 0" class="ms-2 badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle">
                    {{ itensEmbarque.length }} {{ itensEmbarque.length === 1 ? 'produto' : 'produtos' }}
                  </span>
                </h6>
                <div class="d-flex gap-2">
                  <button
                    v-if="podeEditar && pesoChangesCount > 0"
                    type="button"
                    class="btn btn-sm btn-outline-primary fw-bold"
                    @click="salvarTodosPesos"
                    :disabled="isSavingPesos"
                  >
                    <span v-if="isSavingPesos" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="bi bi-save me-1"></i>
                    Salvar Alterações
                  </button>
                </div>
              </div>
              <div class="card-body p-0 border-top border-warning-subtle">
                <ExcelGrid
                  :columns="pesosColumns"
                  :data="itensEmbarque"
                  :loading="isLoadingItens"
                  :readonly="!podeEditar"
                  :listas="listas"
                  :help-enabled="false"
                  :has-changes-field="'idEmbarqueItem'"
                  empty-message="Nenhum produto vinculado a este embarque."
                  @cell-edit="onPesoCellEdit"
                  @show-notification="(p) => emit('show-notification', p)"
                >
                  <template #cell-atribNcm="{ row }">
                    <button
                      type="button"
                      class="btn-icon-minimalista"
                      @click="abrirModalAtributosNcmPeso(row)"
                      :title="temAtributosNcmPreenchidos(row) ? 'Atributos NCM preenchidos. Clique para editar.' : 'Preencher atributos NCM'"
                    >
                      <span v-if="temAtributosNcmPreenchidos(row)" class="badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle" style="cursor: pointer;">
                        <i class="bi bi-check-circle-fill me-1"></i> OK
                      </span>
                      <span v-else class="badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle" style="cursor: pointer;">
                        <i class="bi bi-pencil-fill me-1"></i> Preencher
                      </span>
                    </button>
                  </template>
                  <template #cell-acoes="{ row }">
                    <template v-if="podeEditar && row._pesoDirty">
                      <button type="button" class="btn-icon-minimalista"
                              @click="salvarPesoItem(row)" :disabled="isSavingPesos"
                              title="Salvar pesos">
                        <i class="bi bi-check-lg text-success"></i>
                      </button>
                      <button type="button" class="btn-icon-minimalista"
                              @click="cancelarPesoItem(row)"
                              title="Cancelar alterações">
                        <i class="bi bi-x-lg text-danger"></i>
                      </button>
                    </template>
                    <span v-else class="text-muted small">—</span>
                  </template>
                </ExcelGrid>
              </div>
            </div>
          </div>
        </div>

        <!-- MODAL DE ATRIBUTOS NCM (ABA PESOS) -->
        <ModalFormularioV2
          v-model="showModalAtributosNcmPeso"
          titulo="Atributos do Portal Único (NCM)"
          tamanho="lg"
          :loading="isLoadingAtributosPeso"
          @confirmar="salvarAtributosNcmPeso"
        >
          <div v-if="isLoadingAtributosPeso" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-info me-2"></div>
            Carregando atributos do NCM...
          </div>
          <div v-else-if="atributosNcmPeso.length === 0" class="text-center py-4 text-muted">
            <i class="bi bi-info-circle me-2"></i> Nenhum atributo de escopo OPERACAO encontrado para este NCM.
          </div>
          <div v-else class="row g-3">
            <div class="col-12 mb-2">
              <div class="alert alert-info border-0 shadow-sm small py-2 d-flex align-items-center">
                <i class="bi bi-info-circle-fill me-2 fs-5"></i>
                <span>Preencha os atributos dinâmicos do NCM para esta operação de embarque.</span>
              </div>
            </div>
            <div v-for="attr in atributosNcmPeso" :key="attr.idAtributoNcm" class="col-md-6">
              <CampoInput
                :id="'attr_peso_' + attr.codigoSiscomex"
                :label="attr.descricao"
                v-model="valoresAtributosNcmPeso[attr.codigoSiscomex]"
                :required="attr.obrigatorio"
                :placeholder="'Preencha o valor para ' + attr.codigoSiscomex"
              />
            </div>
          </div>
        </ModalFormularioV2>

        <!-- ABA 3: PACKING LIST -->
        <div class="tab-pane fade" :class="{ show: abaAtiva === 'packingList', active: abaAtiva === 'packingList' }">
          <div v-if="!isEmbarqueSaved" class="text-center py-5">
            <p class="text-muted mb-0">Salve o embarque primeiro para acessar o Packing List.</p>
          </div>

          <div v-else>
            <!-- TABELA DE PACKING LIST -->
            <div class="card mb-3 border-warning-subtle">
              <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center py-2">
                <h6 class="mb-0 text-warning-emphasis fw-bold small">
                  <i class="bi bi-list-check me-1"></i> Itens do Packing List
                  <span v-if="packingListItems.length > 0" class="ms-2 badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle">
                    {{ packingListItems.length }} {{ packingListItems.length === 1 ? 'linha' : 'linhas' }}
                  </span>
                </h6>
                <div class="d-flex gap-2">
                  <button
                    v-if="podeEditar && isEmbarqueSaved"
                    type="button"
                    class="btn btn-sm btn-gradient-ia-moderno fw-bold"
                    @click="showAssistentePackingModal = true"
                  >
                    <i class="bi bi-magic me-1"></i> + Assistente IA
                  </button>
                  <button
                    v-if="podeEditar && isEmbarqueSaved && hasAnyPackingListChanges()"
                    type="button"
                    class="btn btn-sm btn-outline-secondary fw-bold"
                    @click="cancelAllPackingList"
                  >
                    <i class="bi bi-x-lg me-1"></i>
                    Cancelar
                  </button>
                </div>
              </div>
              <div class="card-body p-0 border-top border-warning-subtle">
                <ExcelGrid
                  :columns="packingListColumns"
                  :data="packingListItems"
                  :loading="isLoadingPackingList"
                  empty-message="Nenhum item no packing list. Clique em 'Adicionar Linha' para começar."
                  :row-key="'idEmbarquePackingList'"
                  :row-class="packingListRowClass"
                  :readonly="!podeEditar"
                  :allow-add-rows="podeEditar"
                  :has-changes-field="'idEmbarquePackingList'"
                  :listas="listas"
                  @add-row="adicionarLinhaPackingList"
                  @cell-edit="onPackingListCellEdit"
                  @delete-row="onPackingListDeleteRow"
                  @cancel-all="cancelAllPackingList"
                  @show-notification="(p) => emit('show-notification', p)"
                >
                  <template #cell-_actions="{ row }">
                    <template v-if="!row.flagPreenchimentoConcluido">
                      <button v-if="podeEditar" type="button" class="btn-icon-minimalista"
                              @click="duplicarLinhaPackingList(row)" title="Duplicar linha">
                        <i class="bi bi-copy text-primary"></i>
                      </button>
                      <button v-if="podeEditar" type="button" class="btn-icon-minimalista text-danger"
                              @click="packingListConfirmDelete = row; packingListConfirmModal.show()"
                              title="Excluir linha">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </template>
                    <template v-else>
                      <span class="badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle" title="Preenchimento concluído">
                        <i class="bi bi-check-circle-fill me-1"></i> Concluído
                      </span>
                    </template>
                  </template>
                </ExcelGrid>
              </div>
            </div>
          </div>
        </div>

        <!-- ABA 4: CONTAINERS -->
        <div class="tab-pane fade" :class="{ show: abaAtiva === 'containers', active: abaAtiva === 'containers' }">
          <div v-if="!isEmbarqueSaved" class="text-center py-5">
            <p class="text-muted mb-0">Salve o embarque primeiro para acessar os Containers.</p>
          </div>

          <div v-else>
            <!-- TABELA DE CONTAINERS -->
            <div class="card mb-3 border-warning-subtle">
              <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center py-2">
                <h6 class="mb-0 text-warning-emphasis fw-bold small">
                  <i class="bi bi-boxes me-1"></i> Containers
                  <span v-if="containerItems.length > 0" class="ms-2 badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle">
                    {{ containerItems.length }} {{ containerItems.length === 1 ? 'container' : 'containers' }}
                  </span>
                </h6>
                <div class="d-flex gap-2">
                  <button
                    v-if="podeEditar && isEmbarqueSaved"
                    type="button"
                    class="btn btn-sm btn-gradient-ia-moderno fw-bold"
                    @click="showAssistenteContainerModal = true"
                  >
                    <i class="bi bi-magic me-1"></i> + Assistente IA
                  </button>
                  <button
                    v-if="podeEditar && isEmbarqueSaved && hasAnyContainerChanges()"
                    type="button"
                    class="btn btn-sm btn-outline-secondary fw-bold"
                    @click="cancelAllContainers"
                  >
                    <i class="bi bi-x-lg me-1"></i>
                    Cancelar
                  </button>
                </div>
              </div>
              <div class="card-body p-0 border-top border-warning-subtle">
                <ExcelGrid
                  :columns="containerColumns"
                  :data="containerItems"
                  :loading="isLoadingContainers"
                  :readonly="!podeEditar"
                  :listas="listas"
                  :help-enabled="false"
                  :has-changes-field="'idEmbarqueContainer'"
                  :allow-add-rows="podeEditar"
                  :row-class="containerRowClass"
                  empty-message="Nenhum container cadastrado. Clique em Adicionar Container para começar."
                  @add-row="adicionarLinhaContainer"
                  @cell-edit="onContainerCellEdit"
                  @delete-row="onContainerDeleteRow"
                  @show-notification="(p) => emit('show-notification', p)"
                >
                  <template #cell-_actions="{ row }">
                    <template v-if="!row.conteudo?.length > 0">
                      <button v-if="podeEditar" type="button" class="btn-icon-minimalista"
                              @click="duplicarLinhaContainer(row)" title="Duplicar container">
                        <i class="bi bi-copy text-primary"></i>
                      </button>
                      <button v-if="podeEditar" type="button" class="btn-icon-minimalista text-danger"
                              @click="containerConfirmDelete = row; containerConfirmModal.show()"
                              title="Excluir container">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </template>
                    <template v-else>
                      <span class="badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle" title="Container com packing lists associados">
                        <i class="bi bi-check-circle-fill me-1"></i> Estufado
                      </span>
                    </template>
                  </template>
                </ExcelGrid>
              </div>
            </div>
          </div>
        </div>

        <!-- ABA 5: DISTRIBUIÇÃO DE CARGA -->
        <div class="tab-pane fade" :class="{ show: abaAtiva === 'distribuicao', active: abaAtiva === 'distribuicao' }">
          <div v-if="!isEmbarqueSaved" class="text-center py-5">
            <p class="text-muted mb-0">Salve o embarque primeiro para acessar a Distribuição de Carga.</p>
          </div>
          <template v-else>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="orientacao-uso-smart-mode">
                <i class="bi bi-info-circle-fill text-info me-2"></i>
                <span class="text-muted small">
                  Arraste os <span class="badge bg-warning bg-opacity-25 text-warning-emphasis border border-warning-subtle">Itens do Embarque</span> para os <span class="badge bg-danger bg-opacity-25 text-danger-emphasis border border-danger-subtle">Lotes a Estufar</span>. Após concluir o preenchimento dos lotes, marque-os como concluídos e aloque-os nos containers do <span class="badge bg-primary bg-opacity-25 text-primary-emphasis border border-primary-subtle">Plano de Estufagem</span>.
                </span>
              </div>
              <div class="d-flex gap-2">
                <button
                  v-if="podeEditar && isEmbarqueSaved"
                  type="button"
                  class="btn btn-sm btn-gradient-ia-moderno fw-bold"
                  @click="showAssistenteDistribuicaoModal = true"
                >
                  <i class="bi bi-magic me-1"></i> + Assistente IA
                </button>
                <button
                  v-if="isEmbarqueSaved"
                  type="button"
                  class="btn btn-sm btn-outline-primary fw-bold"
                  @click="$refs.smartModeRef?.abrirRelatorioDistribuicao()"
                >
                  <i class="bi bi-file-earmark-text me-1"></i> Relatório de Estufagem
                </button>
              </div>
            </div>
            <EmbarquesSmartMode
              ref="smartModeRef"
              :itens-embarque="itensEmbarque"
              :packing-list-items="packingListItems"
              :container-items="containerItems"
              :listas="listas"
              :pode-editar="podeEditar"
              :parceiros="parceirosMap"
              :dados-embarque="item"
              @vincular-produto="handleVincularProduto"
              @vincular-container="handleVincularContainer"
              @desvincular-container="handleDesvincularContainer"
              @desvincular-produto="handleDesvincularProduto"
              @toggle-flag-lote="handleToggleFlagLote"
              @show-notification="(p) => emit('show-notification', p)"
            />
          </template>
        </div>

        <!-- ABA 6: ATRAÇÃO -->
        <div class="tab-pane fade" :class="{ show: abaAtiva === 'atracacao', active: abaAtiva === 'atracacao' }"
             v-if="item.lstStatusEmbarque === LOG_ST_EMBARQUE.Transito">
          <div class="card mb-3 border-success-subtle">
            <div class="card-header bg-success-subtle">
              <h5 class="mb-0 text-success-emphasis fw-bold">
                <i class="bi bi-anchor me-2"></i>Dados de Atracação
              </h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-3">
                  <CampoBusca id="idFilialAtracacao" label="Filial"
                    v-model="item.idFilial" :configuracao-busca="configBuscaFilial"
                    required disabled />
                </div>
                <div class="col-md-3">
                  <CampoBusca id="idLocalEstoqueAtracacao" label="Local Estoque (Terminal)"
                    v-model="item.idLocalEstoqueAtracacao"
                    :configuracao-busca="configBuscaLocalEstoqueTerminal"
                    required :disabled="!podeEditar"
                    @show-notification="(p) => emit('show-notification', p)" />
                </div>
                <div class="col-md-3">
                  <CampoInput id="dataAtracacao" label="Data de Atracação"
                    v-model="item.dataAtracacao" type="date"
                    required :disabled="!podeEditar" />
                </div>
                <div class="col-md-3">
                  <CampoInput id="dataPresencaCarga" label="Data de Presença de Carga"
                    v-model="item.dataPresencaCarga" type="date"
                    required :disabled="!podeEditar" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AUDITORIA -->
      <AuditLogViewer
        v-if="mostrarAuditoria"
        tabela="Embarque"
        :chave-primaria="item.idEmbarque"
        :titulo-registro="item.numeroProcessoEmbarque"
        :dicionarios="{ LstStatusEmbarque: listas.LstStatusEmbarque || [] }"
        contexto="tenant"
        @fechar="mostrarAuditoria = false"
      />
    </FormularioCadastro>
    </template>

    <!-- MODAIS DE PRODUTO -->
    <ConfirmationModal
      ref="confirmModal"
      title="Excluir Item"
      message="Deseja realmente excluir este item do embarque?"
      confirm-button-text="Sim, Excluir"
      cancel-button-text="Não, Manter"
      @confirmed="confirmarExclusaoItem"
    />

    <ConfirmationModal
      ref="packingListConfirmModal"
      title="Excluir Item do Packing List"
      message="Deseja realmente excluir este item do packing list?"
      confirm-button-text="Sim, Excluir"
      cancel-button-text="Não, Manter"
      @confirmed="confirmarExclusaoPackingList"
    />

    <ConfirmationModal
      ref="containerConfirmModal"
      title="Excluir Container"
      message="Deseja realmente excluir este container?"
      confirm-button-text="Sim, Excluir"
      cancel-button-text="Não, Manter"
      @confirmed="confirmarExclusaoContainer"
    />

    <ModalFormulario
      v-model="showModalSelecaoItemPacking"
      titulo="Selecionar Item do Embarque para Packing List"
      tamanho="xl"
      ocultar-confirmar
      class="modal-dark-aware"
    >
      <div class="admin-table-container">
        <table class="table table-hover admin-table mb-0">
          <thead>
            <tr>
              <th>Produto</th>
              <th>SKU</th>
              <th class="text-end">Qtd Embarcada (Forn)</th>
              <th>Un (Forn)</th>
              <th class="text-end">Qtd Embarcada (Base)</th>
              <th>Un (Base)</th>
              <th class="text-center" style="width: 80px;">Ação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="itemEmb in itensEmbarque" :key="itemEmb.idEmbarqueItem">
              <td>{{ itemEmb.nomeProduto || '#' + itemEmb.idProduto }}</td>
              <td><code>{{ itemEmb.sku || '-' }}</code></td>
              <td class="text-end">{{ formatarQtd(itemEmb.quantidadeEmbarcadaUnFornecedor) }}</td>
              <td>{{ getTextoLista(listas.LstUnidadeMedidaFornecedor, itemEmb.lstUnidadeMedidaFornecedor || itemEmb.unidadeMedidaFornecedor) }}</td>
              <td class="text-end">{{ formatarQtd(itemEmb.quantidadeEmbarcadaUnBasica) }}</td>
              <td>{{ getTextoLista(listas.LstUnidadeMedidaBasica, itemEmb.lstUnidadeMedidaBasica || itemEmb.unidadeMedidaBasica) }}</td>
              <td class="text-center">
                <button
                  type="button"
                  class="btn btn-sm btn-warning text-dark fw-bold"
                  @click="selecionarItemParaPackingList(itemEmb)"
                >
                  Selecionar
                </button>
              </td>
            </tr>
            <tr v-if="itensEmbarque.length === 0">
              <td colspan="7" class="text-center py-4 text-muted">Nenhum item disponível no embarque.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ModalFormulario>

    <ConfirmationModal
      ref="confirmationModalRef"
      title="Alterações não salvas"
      message="Existem alterações no formulário que serão perdidas se você continuar sem salvar. Deseja realmente continuar?"
      confirm-button-text="Sim, descartar alterações"
      cancel-button-text="Não, voltar e salvar"
      @confirmed="onConfirmTransition"
    />

    <ModalFormulario
      v-model="showModalSelecaoProduto"
      titulo="Selecionar Produto para Embarque"
      tamanho="xl"
      ocultar-confirmar
      class="modal-dark-aware"
    >
      <div class="admin-table-container">
        <table class="table table-hover admin-table mb-0">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Descrição</th>
              <th class="text-end">Qtd na Cota</th>
              <th class="text-end">Saldo Disponível (Forn)</th>
              <th>Unidade (Forn)</th>
              <th class="text-end">Saldo Disponível (Base)</th>
              <th>Unidade Base</th>
              <th class="text-center" style="width: 80px;">Ação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in produtosComSaldo" :key="p.idCotaProduto">
              <td><code>{{ p.sku }}</code></td>
              <td>{{ p.nomeProduto }}</td>
              <td class="text-end">{{ p.quantidadeUnFornecedorNegociada }}</td>
              <td class="text-end fw-bold text-primary">{{ p.saldoDisponivel }}</td>
              <td>{{ getTextoLista(listas.LstUnidadeMedidaFornecedor, p.lstUnidadeMedidaFornecedor) }}</td>
              <td class="text-end fw-bold text-info">{{ p.saldoDisponivelBase }}</td>
              <td>{{ getTextoLista(listas.LstUnidadeMedidaBasica, p.lstUnidadeMedidaBasica) }}</td>
              <td class="text-center">
                <button
                  type="button"
                  class="btn btn-sm btn-warning text-dark fw-bold"
                  @click="selecionarProdutoDaCota(p)"
                  :disabled="p.saldoDisponivel <= 0"
                >
                  Selecionar
                </button>
              </td>
            </tr>
            <tr v-if="produtosComSaldo.length === 0">
              <td colspan="8" class="text-center py-4 text-muted">Nenhum produto encontrado na cota de origem.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ModalFormulario>

    <ModalFormulario
      v-model="showModalFormItem"
      :titulo="itemEmEdicao?.idEmbarqueItem ? (isVisualizacaoModalItem ? 'Visualizar Item do Embarque' : 'Editar Item do Embarque') : 'Detalhes do Item para Embarque'"
      tamanho="lg"
      :ocultar-confirmar="true"
      :loading="isLoadingItens"
      class="modal-dark-aware"
    >
      <template #footer></template>
      <div v-if="itemEmEdicao" class="row g-3">
        <div class="col-12" v-if="itemEmEdicao.nomeProduto">
          <div class="card bg-body-secondary border-0">
            <div class="card-body py-2">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h6 class="mb-0 fw-bold text-warning-emphasis">{{ itemEmEdicao.nomeProduto }}</h6>
                <small class="text-muted">SKU: {{ itemEmEdicao.sku }}</small>
              </div>
              <div v-if="saldoProdutoSelecionado" class="d-flex flex-wrap gap-2 mt-1">
                <span class="badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle">
                  Saldo Pendente (Forn): {{ formatarQtd(saldoProdutoSelecionado.saldoDisponivel) }} {{ getTextoLista(listas.LstUnidadeMedidaFornecedor, itemEmEdicao.lstUnidadeMedidaFornecedor) }}
                </span>
                <span class="badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle">
                  Saldo Pendente (Base): {{ formatarQtd(saldoProdutoSelecionado.saldoDisponivelBase) }} {{ getTextoLista(listas.LstUnidadeMedidaBasica, itemEmEdicao.lstUnidadeMedidaBasica) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Primeira Linha: Parceiro, Nº Lote, Preço Unitário (FOB) -->
        <div class="col-md-6">
          <CampoBusca
            id="idParceiroNegocioProdutor"
            label="Parceiro (Produtor/Fabricante)"
            v-model="itemEmEdicao.idParceiroNegocioProdutor"
            :configuracao-busca="configBuscaParceiroPCat2"
            :disabled="!podeEditar"
          />
        </div>
        <div class="col-md-3" v-if="itemEmEdicao.controlaLote">
          <CampoBusca
            id="idLoteFabricacao"
            label="Lote de Fabricação"
            v-model="itemEmEdicao.idLoteFabricacao"
            :configuracao-busca="configBuscaLoteFabricacao"
            :disabled="!podeEditar || !itemEmEdicao?.idParceiroNegocioProdutor"
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="precoUnitarioMercadoriaFob"
            label="Preço Unitário (FOB)"
            v-model="itemEmEdicao.precoUnitarioMercadoriaFob"
            type="moeda"
            :disabled="!podeEditar"
          />
        </div>

        <!-- Segunda Linha: Qtd Embarcada (Forn), Fator Conversão, Qtd (Un. Básica) -->
        <div class="col-md-4">
          <CampoInput
            id="quantidadeEmbarcadaUnFornecedor"
            label="Quantidade Embarcada (Fornecedor)"
            :model-value="itemEmEdicao.quantidadeEmbarcadaUnFornecedor"
            @update:model-value="onQtdFornecedorUpdate"
            @blur="calcularConversao('fornecedor')"
            dynamic-decimals
            required
            :descricao-informativa="getTextoUnidadeCompleto('LstUnidadeMedidaFornecedor', itemEmEdicao.lstUnidadeMedidaFornecedor)"
          />
        </div>
        <div class="col-md-4">
          <CampoInput
            id="fatorConversao"
            label="Fator de Conversão"
            v-model="itemEmEdicao.fatorConversao"
            type="decimal"
            :disabled="true"
            descricao-informativa="Relação entre unidade básica e fornecedor"
          />
        </div>
        <div class="col-md-4">
          <CampoInput
            id="quantidadeEmbarcadaUnBasica"
            label="Quantidade (Unidade Básica)"
            :model-value="itemEmEdicao.quantidadeEmbarcadaUnBasica"
            @update:model-value="onQtdBasicaUpdate"
            @blur="calcularConversao('base')"
            dynamic-decimals
            :descricao-informativa="getTextoUnidadeCompleto('LstUnidadeMedidaBasica', itemEmEdicao.lstUnidadeMedidaBasica)"
          />
        </div>

        <!-- Mensagem informativa sobre conversão -->
        <div class="col-12" v-if="itemEmEdicao.fatorConversao && parseNumber(itemEmEdicao.fatorConversao) > 0">
          <div class="alert alert-info py-2 mb-0">
            <i class="bi bi-info-circle me-2"></i>
            As quantidades (Fornecedor e Base) são sincronizadas automaticamente pelo fator de conversão.
          </div>
        </div>
        <div class="col-12" v-else-if="itemEmEdicao.fatorConversao !== null">
          <div class="alert alert-warning py-2 mb-0">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Fator de conversão não definido ou zero. As quantidades devem ser informadas independentemente.
          </div>
        </div>

        <!-- Botão Calcular (conversão centralizada no backend) -->
        <div class="col-12 d-flex justify-content-end">
          <button type="button" class="btn btn-sm btn-outline-primary"
            @click="calcularConversao('fornecedor')">
            <i class="bi bi-calculator me-1"></i> Calcular
          </button>
        </div>

        <!-- ATRIBUTOS DINÂMICOS NCM (Portal Único) -->
        <div class="col-12" v-if="atributosNcm.length > 0">
          <div class="card bg-body-tertiary border-0">
            <div class="card-body">
              <h6 class="fw-bold mb-3"><i class="bi bi-list-check me-2"></i>Atributos do Portal Único (NCM)</h6>
              <div class="row g-3">
                <div v-for="attr in atributosNcm" :key="attr.idAtributoNcm" class="col-md-6">
                  <CampoInput
                    :id="'attr_' + attr.codigoSiscomex"
                    :label="attr.descricao"
                    v-model="valoresAtributosNcm[attr.codigoSiscomex]"
                    :required="attr.obrigatorio"
                    :placeholder="'Preencha o valor para ' + attr.codigoSiscomex"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="isLoadingAtributos" class="col-12 text-center py-3">
          <div class="spinner-border spinner-border-sm text-warning me-2"></div>
          Buscando exigências da NCM...
        </div>

        <!-- Ações inline do modal (substituem o rodapé) -->
        <div class="col-12">
          <hr class="my-2" />
          <div class="d-flex justify-content-end gap-2">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="isLoadingItens"
              @click="showModalFormItem = false"
            >
              Fechar
            </button>
            <button
              v-if="!isVisualizacaoModalItem"
              type="button"
              class="btn btn-primary"
              :disabled="isLoadingItens"
              @click="salvarItem"
            >
              <span v-if="isLoadingItens" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-check-lg me-1"></i>
              Salvar
            </button>
          </div>
        </div>
      </div>
    </ModalFormulario>

    <!-- MODAIS ASSISTENTE IA -->
    <AssistentePreenchimentoModal
      v-model="showAssistentePackingModal"
      :id-embarque="item.idEmbarque"
      modo="packing-list"
      @packing-list-salvo="handlePackingListSalvo"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <AssistentePreenchimentoModal
      v-model="showAssistenteContainerModal"
      :id-embarque="item.idEmbarque"
      modo="containers"
      @containers-salvo="handleContainersSalvo"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <AssistentePreenchimentoModal
      v-model="showAssistenteDistribuicaoModal"
      :id-embarque="item.idEmbarque"
      modo="completo"
      @packing-list-salvo="handlePackingListSalvo"
      @containers-salvo="handleContainersSalvo"
      @tudo-salvo="handleTudoSalvo"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <!-- MODAL DE FATURA DO EMBARQUE -->
    <ModalFormulario
      v-model="showModalFatura"
      :titulo="faturaEmEdicao?.idEmbarqueFatura ? (isVisualizacaoModalFatura ? 'Visualizar Fatura do Embarque' : 'Editar Fatura do Embarque') : 'Nova Fatura do Embarque'"
      tamanho="lg"
      :loading="isLoadingFaturas"
      :ocultar-confirmar="isVisualizacaoModalFatura"
      @confirmar="salvarFaturaModal"
      class="modal-dark-aware"
    >
      <div v-if="faturaEmEdicao" class="row g-3">
        <div class="col-md-6">
          <CampoBusca
            id="fatura-idParceiroNegocioEmissor"
            label="Fornecedor (Emissor da Fatura)"
            v-model="faturaEmEdicao.idParceiroNegocioEmissor"
            :configuracao-busca="configBuscaFornecedorFatura"
            :disabled="!podeEditar || isLoadingFornecedores"
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="fatura-numeroProformaInvoice"
            label="Nº Proforma Invoice"
            v-model="faturaEmEdicao.numeroProformaInvoice"
            :disabled="!podeEditar"
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="fatura-dataEmissaoProforma"
            label="Data Proforma"
            v-model="faturaEmEdicao.dataEmissaoProforma"
            type="date"
            :disabled="!podeEditar"
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="fatura-numeroCommercialInvoice"
            label="Nº Commercial Invoice"
            v-model="faturaEmEdicao.numeroCommercialInvoice"
            :disabled="!podeEditar"
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="fatura-dataEmissaoInvoice"
            label="Data Invoice"
            v-model="faturaEmEdicao.dataEmissaoInvoice"
            type="date"
            :disabled="!podeEditar"
          />
        </div>
        <div class="col-md-3">
          <CampoBuscaLista
            id="lstMoeda"
            label="Moeda"
            v-model="faturaEmEdicao.lstMoeda"
            tabela="Embarque"
            :disabled="!podeEditar"
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="fatura-valorTotalFatura"
            label="Valor Total"
            v-model="faturaEmEdicao.valorTotalFatura"
            type="moeda"
            :disabled="!podeEditar"
          />
        </div>
        <div class="col-md-3">
          <CampoSelect
            id="fatura-lstStatusFatura"
            label="Status da Fatura"
            v-model="faturaEmEdicao.lstStatusFatura"
            :options="listas.LstStatusFatura || []"
            option-label="textoLista"
            option-value="idListaClienteItem"
            :disabled="true"
          />
        </div>
        <div class="col-md-3">
          <CampoSelect
            id="fatura-lstStatusFinanceiroFatura"
            label="Status Financeiro"
            v-model="faturaEmEdicao.lstStatusFinanceiroFatura"
            :options="listas.LstStatusFinanceiroFatura || []"
            option-label="textoLista"
            option-value="idListaClienteItem"
            :disabled="true"
          />
        </div>
      </div>
    </ModalFormulario>

    <!-- MODAL: Lançar Despesas (Documentos de Entrada) -->
    <ModalFormulario
      v-model="showModalDocumentosGrid"
      titulo="Lançar Despesas"
      :ocultar-confirmar="true"
      tamanho="xxl"
      @update:model-value="showModalDocumentosGrid = $event"
    >
      <DocumentoEntradaGrid
        ref="documentoEntradaGridRef"
        :id-embarque="item.idEmbarque"
        v-model:documento-selecionado="documentoSelecionado"
        :tem-modulo-documento="true"
        :tem-completo="true"
        :tem-visualizacao="true"
        :pode-editar="true"
        :listas="listas"
        :exibir-primeiro-produto="true"
        :id-organizacao="authStore.empresaAtiva"
        :com-smart-mode="true"
        :id-parceiro-negocio-fornecedor="contextoPai?.idParceiroNegocioFornecedor || null"
        contexto-origem="embarque"
        modo-alocacao="livre"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </ModalFormulario>

    <!-- MODAL: Solicitar Adiantamento -->
    <ModalFormulario
      v-model="showModalSolicitacaoAdiantamento"
      titulo="Solicitações de Adiantamento"
      :ocultar-confirmar="true"
      tamanho="xl"
      @update:model-value="showModalSolicitacaoAdiantamento = $event"
    >
      <SolicitacaoAdiantamentoGrid
        ref="solicitacaoAdiantamentoGridRef"
        :id-embarque="item.idEmbarque"
        :pode-editar="true"
        :pode-visualizar="true"
        :id-organizacao="authStore.empresaAtiva"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </ModalFormulario>
  </div>
</template>

<style scoped>
/* ============================================================ */
/* FIX: Garantir que o formulário não transborde em telas       */
/* estreitas, especialmente quando o drilldown está ativo       */
/* ============================================================ */
.embarques-form-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}

/* Garantir que abas inativas estejam completamente ocultas */
.embarques-form-wrapper :deep(.tab-pane:not(.active)) {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
  position: absolute !important;
  z-index: -1 !important;
}

.embarques-form-wrapper :deep(.tab-pane.active) {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: relative !important;
  z-index: 1 !important;
}

/* Garantir que tabelas não estourem o layout */
.packing-list-table,
.admin-table {
  width: 100%;
  table-layout: fixed;
}

.packing-list-table th,
.packing-list-table td,
.admin-table th,
.admin-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.grid-scroll-container {
  max-height: 200px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  overflow-x: auto;
}

.admin-table td {
  border-bottom: 1px solid var(--bs-border-color);
  color: var(--bs-body-color);
  padding: 0.5rem 0.75rem;
}

/* Compact table style for invoice and items grids */
.table-compact td {
  padding: 0.3rem 0.5rem !important;
  font-size: 0.825rem;
  vertical-align: middle;
}

.table-compact th {
  padding: 0.4rem 0.5rem !important;
  font-size: 0.72rem;
  white-space: nowrap;
}

/* Action hover: hide buttons by default, show on row hover */
.action-hover-cell .action-btn-group {
  opacity: 0;
  transition: opacity 0.15s ease;
}

tr:hover .action-hover-cell .action-btn-group {
  opacity: 1;
}

.action-hover-col {
  width: 80px;
}

/* Smaller icons inside compact table action groups */
.table-compact .action-btn-group .btn-icon-minimalista {
  padding: 0.15rem 0.2rem;
  font-size: 0.75rem;
}

.table-compact .action-btn-group .btn-icon-minimalista i {
  font-size: 0.8rem;
}

/* Lote badge style */
.lote-badge {
  display: inline-block;
  background-color: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
  font-family: 'Consolas', 'Courier New', monospace;
  color: var(--bs-body-color);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.linha-selecionada td {
  background-color: var(--bs-warning-bg-subtle) !important;
  color: var(--bs-warning-text-emphasis) !important;
}

.linha-selecionada code {
  color: var(--bs-warning-text-emphasis) !important;
}

.linha-selecao-fatura td {
  background-color: var(--bs-info-bg-subtle) !important;
  color: var(--bs-info-text-emphasis) !important;
}

.linha-selecao-fatura code {
  color: var(--bs-info-text-emphasis) !important;
}

.btn-icon-minimalista {
  background: none;
  border: none;
  color: #6c7d8e;
  padding: 0.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}

.btn-icon-minimalista:hover {
  color: #212934;
}

[data-bs-theme="dark"] .btn-icon-minimalista:hover {
  color: #fff;
}

/* Modal dark mode awareness */
[data-bs-theme="dark"] .modal-dark-aware :deep(.modal-content),
[data-bs-theme="dark"] .modal-dark-aware :deep(.modal-container) {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
  border-color: var(--bs-border-color);
}

[data-bs-theme="dark"] .modal-dark-aware :deep(.modal-header),
[data-bs-theme="dark"] .modal-dark-aware :deep(.modal-footer) {
  border-color: var(--bs-border-color-translucent);
}

[data-bs-theme="dark"] .modal-dark-aware :deep(.card) {
  background-color: var(--bs-secondary-bg);
  border-color: var(--bs-border-color);
}

[data-bs-theme="dark"] .modal-dark-aware :deep(.table) {
  --bs-table-bg: transparent;
  --bs-table-color: var(--bs-body-color);
  --bs-table-border-color: var(--bs-border-color);
}

[data-bs-theme="dark"] .modal-dark-aware :deep(.admin-table-container) {
  background-color: var(--bs-secondary-bg);
}

[data-bs-theme="dark"] .modal-dark-aware :deep(.text-primary) {
  color: var(--bs-primary-border-subtle) !important;
}

.border-warning-subtle {
  border-color: var(--bs-warning-border-subtle) !important;
}

.bg-warning-subtle {
  background-color: var(--bs-warning-bg-subtle) !important;
}

.text-warning-emphasis {
  color: var(--bs-warning-text-emphasis) !important;
}

/* PACKING LIST TABLE STYLES */
.packing-list-table-wrap {
  overflow-x: auto;
}

.packing-list-table {
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
}

.packing-list-table th {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  background-color: var(--bs-tertiary-bg);
  border-bottom: 2px solid var(--bs-border-color);
  padding: 0.35rem 0.4rem;
  border-right: 1px solid var(--bs-border-color-translucent);
}

.packing-list-table th:last-child {
  border-right: none;
}

.packing-list-table td {
  padding: 0.2rem 0.4rem;
  vertical-align: middle;
  border-bottom: 1px solid var(--bs-border-color);
  border-right: 1px solid var(--bs-border-color-translucent);
}

.packing-list-table td:last-child {
  border-right: none;
}

.packing-list-row:hover {
  background-color: var(--bs-warning-bg-subtle) !important;
}

.packing-list-row td {
  font-size: 0.8rem;
}

/* Grid input style (Excel-like) */
.grid-input {
  width: 100%;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 2px 6px;
  transition: all 0.15s ease;
  font-size: 0.8rem;
}

.grid-input:hover {
  border-color: var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
}

.grid-input:focus {
  outline: none;
  border-color: var(--bs-warning);
  background-color: var(--bs-body-bg);
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.15);
}

.grid-input.font-mono {
  font-family: 'Consolas', 'Courier New', monospace;
}

.grid-select {
  appearance: auto;
  background-image: none;
  padding-right: 4px;
}

/* Botão gradiente moderno do Assistente IA (roxo/indigo) */
.btn-gradient-ia-moderno {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
  color: #ffffff !important;
  border: none;
  transition: all 0.3s ease;
  letter-spacing: 0.02em;
  font-size: 0.8rem;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.btn-gradient-ia-moderno:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4) !important;
  color: #ffffff !important;
}

.btn-gradient-ia-moderno:active {
  transform: translateY(0);
}

.btn-gradient-ia-moderno:disabled {
  opacity: 0.5;
  transform: none;
  box-shadow: none !important;
}

[data-bs-theme="dark"] .btn-gradient-ia-moderno {
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 50%, #c084fc 100%);
}

/* ============================================================ */
/* ABAS PRINCIPAIS - Fundo transparente, realce por aba        */
/* ============================================================ */
.embarques-form-wrapper :deep(.nav-tabs) {
  background-color: transparent !important;
  border-bottom: 1px solid var(--bs-warning-border-subtle);
  border-radius: 8px 8px 0 0;
  padding: 4px 4px 0 4px;
  gap: 2px;
}

/* Abas inativas - fundo creme claro, texto dourado escuro */
.embarques-form-wrapper :deep(.nav-tabs .nav-link) {
  border: 1px solid #f0e7d0;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  color: #7d6930;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.5rem 1rem;
  transition: all 0.2s ease;
  background-color: #fffff5;
  margin-bottom: 0;
}

.embarques-form-wrapper :deep(.nav-tabs .nav-link:hover) {
  background-color: #fffef0;
  border-color: #f0e7d0;
  color: #5a4a1a;
  isolation: isolate;
}

/* Aba ativa - fundo warning-subtle (amarelo fraco destacado) */
.embarques-form-wrapper :deep(.nav-tabs .nav-link.active) {
  background-color: var(--bs-warning-bg-subtle) !important;
  border-color: var(--bs-warning-border-subtle) var(--bs-warning-border-subtle) var(--bs-warning-bg-subtle) !important;
  color: var(--bs-warning-text-emphasis) !important;
  font-weight: 700;
  box-shadow: 0 -1px 4px rgba(255, 193, 7, 0.12);
}

.embarques-form-wrapper :deep(.tab-content) {
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-warning-border-subtle);
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 1rem;
}

.embarques-form-wrapper :deep(.tab-pane) {
  min-height: 200px;
}

/* Dark mode: tons escuros */
[data-bs-theme="dark"] .embarques-form-wrapper :deep(.nav-tabs) {
  background-color: transparent !important;
  border-bottom-color: var(--bs-warning-border-subtle);
}

[data-bs-theme="dark"] .embarques-form-wrapper :deep(.nav-tabs .nav-link) {
  background-color: #2a2510;
  border-color: #4a4020;
  color: #c4b070;
}

[data-bs-theme="dark"] .embarques-form-wrapper :deep(.nav-tabs .nav-link:hover) {
  background-color: #3a3020;
  border-color: #4a4020;
  color: #e0cc80;
}

[data-bs-theme="dark"] .embarques-form-wrapper :deep(.nav-tabs .nav-link.active) {
  background-color: var(--bs-warning-bg-subtle) !important;
  border-color: var(--bs-warning-border-subtle) var(--bs-warning-border-subtle) var(--bs-warning-bg-subtle) !important;
  color: var(--bs-warning-text-emphasis) !important;
}

[data-bs-theme="dark"] .embarques-form-wrapper :deep(.tab-content) {
  background-color: var(--bs-body-bg);
  border-color: var(--bs-warning-border-subtle);
}
/* ============================================================ */
/* CABEÇALHOS DOS FRAMES DENTRO DAS ABAS - creme claro         */
/* ============================================================ */
.embarques-form-wrapper :deep(.tab-content .card-header) {
  background-color: #fffff5 !important;
}

.embarques-form-wrapper :deep(.tab-content .card-header .text-warning-emphasis) {
  color: #7d6930 !important;
}

.embarques-form-wrapper :deep(.tab-content .card-header h5),
.embarques-form-wrapper :deep(.tab-content .card-header h6) {
  color: #7d6930 !important;
}

[data-bs-theme="dark"] .embarques-form-wrapper :deep(.tab-content .card-header) {
  background-color: #2a2510 !important;
}

[data-bs-theme="dark"] .embarques-form-wrapper :deep(.tab-content .card-header .text-warning-emphasis) {
  color: #c4b070 !important;
}

[data-bs-theme="dark"] .embarques-form-wrapper :deep(.tab-content .card-header h5),
[data-bs-theme="dark"] .embarques-form-wrapper :deep(.tab-content .card-header h6) {
  color: #c4b070 !important;
}

/* Orientação de uso do Smart Mode */
.orientacao-uso-smart-mode {
  display: flex;
  align-items: flex-start;
  max-width: 70%;
  line-height: 1.5;
}

.orientacao-uso-smart-mode .badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25em 0.5em;
}

/* Estilo para linhas do packing list com preenchimento concluído */
.row-preenchimento-concluido {
  background-color: var(--bs-success-bg-subtle) !important;
  opacity: 0.9;
}

.row-preenchimento-concluido:hover {
  background-color: var(--bs-success-bg-subtle) !important;
  cursor: not-allowed;
}

.row-preenchimento-concluido .grid-input {
  background-color: transparent;
  cursor: not-allowed;
}

.row-preenchimento-concluido .grid-input:hover {
  border-color: transparent;
  background-color: transparent;
}

/* Estilo para linhas de containers com packing lists associados */
.row-container-com-conteudo {
  background-color: var(--bs-success-bg-subtle) !important;
  opacity: 0.9;
}

.row-container-com-conteudo:hover {
  background-color: var(--bs-success-bg-subtle) !important;
  cursor: not-allowed;
}

.row-container-com-conteudo .grid-input {
  background-color: transparent;
  cursor: not-allowed;
}

.row-container-com-conteudo .grid-input:hover {
  border-color: transparent;
  background-color: transparent;
}

/* ============================================================ */
/* ABA PESOS DOS PRODUTOS                                       */
/* ============================================================ */
.pesos-produto-row:hover {
  background-color: var(--bs-info-bg-subtle) !important;
}

.pesos-sku {
  font-size: 0.75rem;
  font-weight: 700;
}

.pesos-qtd {
  font-size: 0.8rem;
  white-space: nowrap;
}
</style>
